import { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import Footer from '../components/Footer';
import MetaHead from '../components/MetaHead';
// TEMP — réactiver avec le composant ci-dessous
// import LanguageSwitcher from '../components/LanguageSwitcher/LanguageSwitcher';
import { storage } from '../utils/platform';
import { PLAN_BY_ID } from '../constants/plans';
import { formatPriceParts } from '../utils/format';

// ════════════════════════════════════════════════════════════════════════════
// useCountUp — anime un nombre depuis 0 jusqu'à target en `duration` ms.
// `isActive` (boolean) déclenche l'animation. `decimals` contrôle la précision.
// Respecte prefers-reduced-motion : retourne target immédiatement si reduce.
// rAF garantit 60fps + cleanup propre au unmount/désactivation.
// ════════════════════════════════════════════════════════════════════════════
function useCountUp(target, duration, isActive, decimals = 0) {
  const [value, setValue] = useState(0);
  const rafRef = useRef(null);
  const startRef = useRef(null);

  useEffect(() => {
    if (!isActive) return undefined;

    // Reduced motion → pas d'anim, valeur finale tout de suite.
    const mql = typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null;
    if (mql && mql.matches) {
      setValue(target);
      return undefined;
    }

    startRef.current = null;
    const tick = (ts) => {
      if (startRef.current === null) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const t = Math.min(1, elapsed / duration);
      // Ease-out cubic (légère décélération en fin de course)
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(target * eased);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setValue(target);
      }
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [target, duration, isActive]);

  // Formatage : virgule française pour les décimales (cohérent avec « 4,6 » du design)
  if (decimals === 0) return Math.round(value).toString();
  return value.toFixed(decimals).replace('.', ',');
}

export default function Landing() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['landing', 'common']);
  const lang = i18n.language || 'fr';

  // Prix de la grille : jamais en dur — ils viennent de constants/plans.js et
  // sont formatés selon la locale (« 9,99 € » en fr/es/it, « €9.99 » en en).
  const renderAmount = (price) => {
    const { value, currency, currencyFirst } = formatPriceParts(price, lang);
    return (
      <span className="lv5-amount">{currencyFirst ? `${currency}${value}` : `${value}${currency}`}</span>
    );
  };
  const [menuOpen, setMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  // Counter-up déclenché quand le mock dashboard entre en viewport
  const [dashVisible, setDashVisible] = useState(false);
  const chartPathRef = useRef(null);

  const noteAnim = useCountUp(4.6, 1100, dashVisible, 1);
  const avisAnim = useCountUp(23, 900, dashVisible, 0);
  const satisAnim = useCountUp(87, 1000, dashVisible, 0);

  // 1. Auth redirect — si déjà loggé, on file au dashboard
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = await storage.get('token');
      if (cancelled) return;
      if (token) navigate('/dashboard');
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  // 2. Mobile menu — body lock + Escape close
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // 3. Sticky nav — toggle classe scrolled quand on scroll
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // 4. Scroll-driven orange veil + halo phone + parallax hero
  // (Anciennement aussi #phoneShowcase, retiré : le téléphone vit désormais dans le hero, le veil ne doit plus s'allumer en haut.)
  //
  // Étendu (PR feat/landing-scroll-anims-footer-lang) :
  // - A.1 : --lv5-phone-halo (0 → 1) — opacity du halo orange autour du téléphone.
  //         Monte au fur et à mesure du scroll dans le hero, redescend quand le phone sort.
  // - A.3 : --lv5-phone-parallax (-40 → +40 px) — translateY de .lv5-hero-visual.
  //         Désactivé sous prefers-reduced-motion (vestibular safety).
  // Un seul scroll listener + rAF pour rester ≤16ms/frame.
  useEffect(() => {
    const root = document.documentElement;
    let ticking = false;

    const reduceMotion =
      typeof window !== 'undefined' && window.matchMedia
        ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
        : false;

    const updateAll = () => {
      const vh = window.innerHeight;

      // — Veil : s'allume quand le centre d'une section trigger approche le
      //   centre du viewport. Cibles : #dashboard (historique) + #problem
      //   (ajout 2026-07-05). On prend le MAX des proximités → chaque
      //   section allume le fond indépendamment, avec fondu croisé naturel.
      //   ⚠️ #faq retiré le jour même : dernière section de la page → le veil
      //   restait allumé sur le footer et le rendait illisible. Le CSS
      //   lisibilité "body.lv5-veil-high .lv5-faq ..." est conservé : pour
      //   réactiver, il suffit de remettre 'faq' dans la liste ci-dessous.
      {
        const viewportCenter = vh / 2;
        const maxDistance = vh * 0.7;
        let pRaw = 0;
        for (const id of ['problem', 'dashboard']) {
          const el = document.getElementById(id);
          if (!el) continue;
          const rect = el.getBoundingClientRect();
          const sectionCenter = rect.top + rect.height / 2;
          const distance = Math.abs(sectionCenter - viewportCenter);
          pRaw = Math.max(pRaw, 1 - distance / maxDistance);
        }
        let p = Math.max(0, Math.min(1, pRaw));
        p = p * p * (3 - 2 * p); // smoothstep
        root.style.setProperty('--lv5-veil', p.toFixed(3));
        document.body.classList.toggle('lv5-veil-high', p > 0.35);
      }

      // — Halo + parallax phone (hero)
      const stage = document.querySelector('.lv5-phone-stage');
      if (stage) {
        const rect = stage.getBoundingClientRect();
        const stageCenter = rect.top + rect.height / 2;
        const distFromCenter = Math.abs(stageCenter - vh / 2);
        const haloRange = vh * 0.9;
        // 0 quand le phone est loin du centre viewport, 1 quand il est au centre.
        let halo = 1 - distFromCenter / haloRange;
        halo = Math.max(0, Math.min(1, halo));
        halo = halo * halo * (3 - 2 * halo); // smoothstep
        root.style.setProperty('--lv5-phone-halo', halo.toFixed(3));

        // Parallax : translateY proportionnel à la position du stage dans la fenêtre.
        // Centre stage à vh/2 → offset 0. Stage en haut → -40. Stage en bas → +40.
        if (!reduceMotion) {
          const norm = (stageCenter - vh / 2) / vh; // ~[-1, +1]
          const clamped = Math.max(-1, Math.min(1, norm));
          const offset = clamped * 40; // px
          root.style.setProperty('--lv5-phone-parallax', `${offset.toFixed(1)}px`);
        }
      }

      // — Hero glow (Fix 4) : opacité 1 quand hero entièrement visible (top dans viewport),
      // décroît proportionnellement au prorata de la sortie par le haut. = 0 quand bottom ≤ 0.
      const hero = document.querySelector('.lv5-hero');
      if (hero) {
        const rect = hero.getBoundingClientRect();
        let glow = Math.max(0, Math.min(1, rect.bottom / rect.height));
        glow = glow * glow * (3 - 2 * glow); // smoothstep
        root.style.setProperty('--lv5-hero-glow', glow.toFixed(3));
      }
    };

    const onScroll = () => {
      if (ticking) return;
      requestAnimationFrame(() => { updateAll(); ticking = false; });
      ticking = true;
    };

    updateAll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', updateAll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', updateAll);
      root.style.removeProperty('--lv5-veil');
      root.style.removeProperty('--lv5-phone-halo');
      root.style.removeProperty('--lv5-phone-parallax');
      root.style.removeProperty('--lv5-hero-glow');
      document.body.classList.remove('lv5-veil-high');
    };
  }, []);

  // 4ter. Hero glow fade-in au load (Fix 4)
  // Anime --lv5-hero-glow-in de 0 à 1 sur 1.4s ease-out. Multiplié avec
  // --lv5-hero-glow (piloté par scroll) dans le CSS du pseudo .lv5-hero::before.
  // Reduced-motion : skip (CSS @media met opacity figée à .35).
  useEffect(() => {
    const root = document.documentElement;
    const reduceMotion =
      typeof window !== 'undefined' && window.matchMedia
        ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
        : false;
    if (reduceMotion) return undefined;

    root.style.setProperty('--lv5-hero-glow-in', '0');
    let rafId = null;
    let start = null;
    const duration = 1400;
    const tick = (ts) => {
      if (start === null) start = ts;
      const t = Math.min(1, (ts - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
      root.style.setProperty('--lv5-hero-glow-in', eased.toFixed(3));
      if (t < 1) rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      root.style.removeProperty('--lv5-hero-glow-in');
    };
  }, []);

  // 4bis. IntersectionObserver unique pour :
  // - A.4 reveal sections (.lv5-reveal → .is-visible)
  // - A.6 déclenchement counter-up KPIs (quand .lv5-dashboard-section visible)
  // - A.7 trace SVG du chart (longueur path → dashoffset 0)
  // Trigger one-shot : on disconnect dès qu'une cible passe is-visible.
  useEffect(() => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      // Fallback : on rend tout visible direct (pas d'anim mais pas de page blanche)
      document.querySelectorAll('.lv5-reveal').forEach((el) => el.classList.add('is-visible'));
      setDashVisible(true);
      return undefined;
    }

    const targets = Array.from(document.querySelectorAll('.lv5-reveal'));
    if (targets.length === 0) return undefined;

    // Préparer le chart SVG : calculer la longueur exacte du path
    // pour un trace propre (stroke-dasharray = totalLength).
    // Fix 2 — Hypothèse D : double rAF pour garantir que le SVG est layouté
    // (sur iOS Safari, getTotalLength() peut renvoyer 0 si appelé trop tôt).
    const path = chartPathRef.current;
    const initPath = () => {
      if (path && typeof path.getTotalLength === 'function') {
        const len = path.getTotalLength();
        if (len > 0) {
          path.style.strokeDasharray = `${len}`;
          path.style.strokeDashoffset = `${len}`;
        }
      }
    };
    requestAnimationFrame(() => requestAnimationFrame(initPath));

    // Fix 2 — Hypothèse A : rootMargin: '-10%' rend le trigger imprévisible
    // sur iOS Safari à cause de son dynamic viewport (la barre du bas qui se
    // masque modifie innerHeight). On passe à rootMargin neutre + threshold
    // plus permissif (0.1 au lieu de 0.15) → trigger plus robuste sur mobile.
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const el = entry.target;
          // Fix 2 — défense supplémentaire : si la section est entièrement
          // passée au-dessus du viewport (cas scroll-rapide / scrollIntoView),
          // on l'active même si !isIntersecting pour éviter qu'elle reste
          // invisible à jamais.
          const passedAbove = entry.boundingClientRect.bottom <= 0;
          if (!entry.isIntersecting && !passedAbove) return;
          el.classList.add('is-visible');

          // Si c'est la section dashboard → on déclenche le counter-up + trace chart
          if (el.classList.contains('lv5-dashboard-section')) {
            setDashVisible(true);
            if (path) {
              // Trace anim : 1.2s ease-out
              path.style.transition = 'stroke-dashoffset 1.2s cubic-bezier(.2,.8,.2,1)';
              path.style.strokeDashoffset = '0';
            }
          }

          io.unobserve(el);
        });
      },
      { threshold: 0.1, rootMargin: '0px' }
    );

    targets.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  // 5. Phone scene cycler — QR → Satisfaction → Google → loop
  useEffect(() => {
    const scenes = document.querySelectorAll('#phoneScreen .lv5-scene');
    if (scenes.length < 2) return;
    const durations = [2800, 3200, 3000]; // QR · Satisfaction · Google
    let idx = 0;
    let timeoutId;

    const next = () => {
      const current = idx;
      const nextIdx = (idx + 1) % scenes.length;
      scenes[current].classList.remove('is-active');
      scenes[nextIdx].classList.add('is-active');
      idx = nextIdx;
      timeoutId = setTimeout(next, durations[nextIdx]);
    };

    timeoutId = setTimeout(next, durations[0]);
    return () => clearTimeout(timeoutId);
  }, []);

  const scrollTo = (id) => {
    setMenuOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const goRegister = (plan) => {
    setMenuOpen(false);
    navigate(plan ? `/register?plan=${plan}` : '/register');
  };

  const goLogin = () => {
    setMenuOpen(false);
    navigate('/login');
  };

  // FAQ : on lit la liste depuis landing.faq.items (returnObjects pour récupérer
  // le tableau d'objets {q, a}). Les balises <em>/<strong> dans `a` sont
  // rendues via <Trans> côté JSX pour éviter tout dangerouslySetInnerHTML.
  const faqs = t('faq.items', { returnObjects: true });
  const faqList = Array.isArray(faqs) ? faqs : [];

  // Pattern QR stable (8x8) utilisé dans les mockups
  const qrPattern = [
    1,1,1,0,1,1,1,1,
    1,0,1,1,0,1,0,1,
    1,1,0,1,1,0,1,1,
    0,1,1,0,1,1,1,0,
    1,0,1,1,0,1,0,1,
    1,1,1,0,1,0,1,1,
    1,0,0,1,1,1,0,1,
    1,1,1,1,0,1,1,1
  ];

  return (
    <>
      <MetaHead page="landing" />
      <style>{landingCss}</style>

      {/* ═══ BACKGROUNDS — fixed, behind everything ═══ */}
      <div className="lv5-aurora" aria-hidden="true"><span /></div>
      <div className="lv5-orange-veil" aria-hidden="true" />
      <div className="lv5-grain" aria-hidden="true" />

      {/* ═══ MOBILE MENU OVERLAY ═══ */}
      <div className={'lv5-mobile-menu' + (menuOpen ? ' open' : '')}>
        <button
          className="lv5-mm-close"
          onClick={() => setMenuOpen(false)}
          aria-label={t('common:nav.close_menu') || 'Fermer le menu'}
        >×</button>
        <button className="lv5-mm-link" onClick={() => scrollTo('how')}>{t('common:nav.how_it_works')}<span>→</span></button>
        <button className="lv5-mm-link" onClick={() => scrollTo('pricing')}>{t('common:nav.pricing')}<span>→</span></button>
        <Link to="/blog" className="lv5-mm-link" onClick={() => setMenuOpen(false)}>{t('common:nav.blog')}<span>→</span></Link>
        <button className="lv5-mm-link" onClick={() => scrollTo('faq')}>{t('common:nav.faq')}<span>→</span></button>
        <Link to="/score-rps" className="lv5-mm-link" onClick={() => setMenuOpen(false)}>{t('common:nav.rps')}<span>→</span></Link>
        <button className="lv5-mm-link" onClick={goLogin}>{t('common:nav.login')}<span>→</span></button>
        <button className="lv5-mm-cta" onClick={() => goRegister()}>{t('common:nav.free_trial')} →</button>
      </div>

      <div className="lv5">
        <div className="lv5-wrap">

          {/* ═══ NAVBAR ═══ */}
          <nav className={'lv5-nav' + (scrolled ? ' scrolled' : '')}>
            <div className="lv5-nav-inner">
              <button
                className="lv5-logo"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                aria-label={t('common:nav.back_to_top')}
              >
                <div className="lv5-logo-mark">R</div>
                <span>Revyo</span>
              </button>

              <div className="lv5-nav-pill">
                <button onClick={() => scrollTo('how')}>{t('common:nav.how_it_works')}</button>
                <button onClick={() => scrollTo('pricing')}>{t('common:nav.pricing')}</button>
                <Link to="/blog">{t('common:nav.blog')}</Link>
              </div>

              <div className="lv5-nav-cta">
                <button
                  className={'lv5-mobile-trigger' + (menuOpen ? ' open' : '')}
                  onClick={() => setMenuOpen(!menuOpen)}
                  aria-label={t('common:nav.menu')}
                >
                  <span /><span /><span />
                </button>
                {/* TEMP — masqué pendant phase test i18n, détection IP only. Réactiver en décommentant. */}
                {/* <LanguageSwitcher compact /> */}
                <button className="lv5-btn-ghost" onClick={goLogin}>{t('common:nav.login')}</button>
                <button className="lv5-btn-primary" onClick={() => goRegister()}>{t('common:nav.free_trial')}</button>
                <Link to="/score-rps" className="lv5-btn-rps">{t('common:nav.rps')}</Link>
              </div>
            </div>
          </nav>

          {/* ═══ HERO — 2 cols: text left / animated phone right ═══ */}
          <section className="lv5-hero">
            <div className="lv5-hero-text">
              <h1 className="lv5-h1">
                {t('hero.title_line1')}<br />{t('hero.title_line2')}{' '}
                <span className="lv5-accent">{t('hero.title_accent')}</span>
              </h1>

              <p className="lv5-sub">
                <Trans i18nKey="landing:hero.subtitle" components={{ strong: <strong /> }} />
              </p>

              <div className="lv5-cta-row">
                <button className="lv5-cta-main" onClick={() => goRegister()}>
                  {t('hero.cta_main')}
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7" /></svg>
                </button>
                <button className="lv5-cta-secondary" onClick={() => scrollTo('pricing')}>{t('hero.cta_secondary')}</button>
              </div>

              <div className="lv5-trust">
                <span className="lv5-trust-item"><span className="lv5-check">✓</span>{t('common:trust.fourteen_days_free')}</span>
                <span className="lv5-trust-item"><span className="lv5-check">✓</span>{t('common:trust.five_min_install')}</span>
                <span className="lv5-trust-item"><span className="lv5-check">✓</span>{t('common:trust.cancel_one_click')}</span>
              </div>

              {/* Carte RPS — lead magnet (préservée, restylée dark glassmorphism) */}
              <Link to="/score-rps" className="lv5-rps-card">
                <div className="lv5-rps-icon">🎯</div>
                <div className="lv5-rps-text">
                  <div className="lv5-rps-title">
                    {t('hero.rps_card_title')} <span className="lv5-rps-accent">{t('hero.rps_card_accent')}</span>
                  </div>
                  <div className="lv5-rps-sub">{t('hero.rps_card_subtitle')}</div>
                </div>
                <span className="lv5-rps-arrow">→</span>
              </Link>
            </div>

            <div className="lv5-hero-visual">
              <div className="lv5-phone-stage">
                <div className="lv5-phone">
                  <div className="lv5-phone-notch" />
                  <div className="lv5-phone-screen" id="phoneScreen">

                    {/* Scene 1 — QR scan futuriste */}
                    <div className="lv5-scene lv5-scene-qr is-active">
                      <div className="lv5-resto-head">
                        <div className="lv5-resto-logo">LC</div>
                        <div>
                          <div className="lv5-resto-name">Le Café Provençal</div>
                          <div className="lv5-resto-loc">{t('phoneShowcase.scene1.resto_loc')}</div>
                        </div>
                      </div>
                      <div className="lv5-qr-scene">
                        <div className="lv5-qr-scene-eyebrow">{t('phoneShowcase.scene1.scan_label')}</div>
                        <div className="lv5-qr-wrap">
                          <div className="lv5-qr-scene-big">
                            {qrPattern.map((v, i) => (
                              <span key={i} className={v ? '' : 'off'} />
                            ))}
                          </div>
                          <div className="lv5-qr-scan-line" />
                          <span className="lv5-qrc lv5-qrc-tl" />
                          <span className="lv5-qrc lv5-qrc-tr" />
                          <span className="lv5-qrc lv5-qrc-bl" />
                          <span className="lv5-qrc lv5-qrc-br" />
                        </div>
                        <div className="lv5-qr-scene-cta">{t('phoneShowcase.scene1.qr_cta')}</div>
                        <div className="lv5-qr-scene-url">scan.revyo.fr/lcp</div>
                      </div>
                      <div className="lv5-qr-flash" />
                      <div className="lv5-powered">{t('phoneShowcase.powered_by')} <b>Revyo</b></div>
                    </div>

                    {/* Scene 2 — Satisfaction */}
                    <div className="lv5-scene lv5-scene-satisfaction">
                      <div className="lv5-resto-head">
                        <div className="lv5-resto-logo">LC</div>
                        <div>
                          <div className="lv5-resto-name">Le Café Provençal</div>
                          <div className="lv5-resto-loc">{t('phoneShowcase.scene2.resto_loc')}</div>
                        </div>
                      </div>
                      <div className="lv5-resto-q">
                        <h3>{t('phoneShowcase.scene2.question_title')}</h3>
                        <p>{t('phoneShowcase.scene2.question_sub')}</p>
                      </div>
                      <div className="lv5-options">
                        <div className="lv5-opt lv5-opt-excellent">
                          <div className="lv5-opt-emoji">😊</div>
                          <div className="lv5-opt-text">
                            <div className="lv5-opt-title">{t('phoneShowcase.scene2.excellent_title')}</div>
                            <div className="lv5-opt-sub">{t('phoneShowcase.scene2.excellent_sub')}</div>
                          </div>
                          <div className="lv5-opt-arrow">→</div>
                        </div>
                        <div className="lv5-opt">
                          <div className="lv5-opt-emoji">😐</div>
                          <div className="lv5-opt-text">
                            <div className="lv5-opt-title">{t('phoneShowcase.scene2.correct_title')}</div>
                            <div className="lv5-opt-sub">{t('phoneShowcase.scene2.correct_sub')}</div>
                          </div>
                          <div className="lv5-opt-arrow">→</div>
                        </div>
                        <div className="lv5-opt">
                          <div className="lv5-opt-emoji">🙁</div>
                          <div className="lv5-opt-text">
                            <div className="lv5-opt-title">{t('phoneShowcase.scene2.decu_title')}</div>
                            <div className="lv5-opt-sub">{t('phoneShowcase.scene2.decu_sub')}</div>
                          </div>
                          <div className="lv5-opt-arrow">→</div>
                        </div>
                      </div>
                      <div className="lv5-powered">{t('phoneShowcase.powered_by')} <b>Revyo</b></div>
                    </div>

                    {/* Scene 3 — Google final */}
                    <div className="lv5-scene lv5-scene-google">
                      <div className="lv5-google-scene">
                        <div className="lv5-google-scene-emoji">🌟</div>
                        <div className="lv5-google-scene-title">{t('phoneShowcase.scene3.thanks_title')}</div>
                        <div className="lv5-google-scene-sub">{t('phoneShowcase.scene3.thanks_sub')}</div>
                        <div className="lv5-google-block">
                          <div className="lv5-google-icon">G</div>
                          <div className="lv5-google-info">
                            <strong>Le Café Provençal</strong>
                            <span>{t('phoneShowcase.scene3.google_resto_loc')}</span>
                          </div>
                        </div>
                        <button className="lv5-google-cta">{t('phoneShowcase.scene3.google_cta')}</button>
                        <div className="lv5-google-redirect">{t('phoneShowcase.scene3.redirect_notice')}</div>
                        <div className="lv5-progress-dots">
                          <div className="lv5-pd done" />
                          <div className="lv5-pd done" />
                          <div className="lv5-pd active" />
                          <div className="lv5-pd" />
                        </div>
                      </div>
                      <div className="lv5-powered">{t('phoneShowcase.powered_by')} <b>Revyo</b></div>
                    </div>

                  </div>
                </div>

                <div className="lv5-float-card lv5-float-tr lv5-float-cascade">
                  <div className="lv5-float-icon lv5-icon-star">★</div>
                  <div>
                    <div className="lv5-float-label">{t('hero.float_rating.label')}</div>
                    <div className="lv5-float-value">
                      {t('hero.float_rating.value')}
                      <span className="lv5-float-delta">{t('hero.float_rating.delta')}</span>
                    </div>
                  </div>
                </div>

                <div className="lv5-float-card lv5-float-bl lv5-float-cascade">
                  <div className="lv5-float-icon lv5-icon-g">G</div>
                  <div>
                    <div className="lv5-float-label">{t('hero.float_reviews.label')}</div>
                    <div className="lv5-float-value">{t('hero.float_reviews.value')}</div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ═══ PROBLÈME — préservée, restylée dark ═══ */}
          <section className="lv5-problem lv5-reveal" id="problem">
            <div className="lv5-problem-intro">
              <h2 className="lv5-problem-h2">{t('problem.title')}</h2>
              <p className="lv5-problem-sub">{t('problem.subtitle')}</p>
            </div>

            <div className="lv5-problem-grid">
              <article className="lv5-prob-card lv5-stagger-child">
                <div className="lv5-prob-ic">
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H8l-5 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
                </div>
                <h3 className="lv5-prob-title">{t('problem.card1_title')}</h3>
                <p className="lv5-prob-text"><Trans i18nKey="landing:problem.card1_text" components={{ em: <em /> }} /></p>
              </article>

              <article className="lv5-prob-card lv5-stagger-child">
                <div className="lv5-prob-ic warn">
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H8l-5 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /><line x1="12" y1="7" x2="12" y2="12" /><line x1="12" y1="15.2" x2="12" y2="15.3" /></svg>
                </div>
                <h3 className="lv5-prob-title">{t('problem.card2_title')}</h3>
                <p className="lv5-prob-text"><Trans i18nKey="landing:problem.card2_text" components={{ em: <em /> }} /></p>
              </article>

              <article className="lv5-prob-card lv5-stagger-child">
                <div className="lv5-prob-ic down">
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 7 9 13 13 9 21 17" /><polyline points="21 12 21 17 16 17" /></svg>
                </div>
                <h3 className="lv5-prob-title">{t('problem.card3_title')}</h3>
                <p className="lv5-prob-text"><Trans i18nKey="landing:problem.card3_text" components={{ em: <em /> }} /></p>
              </article>
            </div>

            <div className="lv5-problem-bridge">
              <p className="lv5-problem-bridge-text">{t('problem.bridge')}</p>
            </div>
          </section>

          {/* ═══ HOW IT WORKS ═══ */}
          <section className="lv5-how lv5-reveal" id="how">
            <div className="lv5-section-head">
              <div className="lv5-section-eyebrow">{t('how.eyebrow')}</div>
              <h2>{t('how.title_main')} <span className="lv5-dim">{t('how.title_dim')}</span></h2>
              <p>{t('how.subtitle')}</p>
            </div>

            <div className="lv5-steps">
              <div className="lv5-step lv5-stagger-child">
                <div className="lv5-step-num">{t('how.step_label')} 01</div>
                <div className="lv5-step-title">{t('how.step1_title')}</div>
                <div className="lv5-step-desc">{t('how.step1_desc')}</div>
                <div className="lv5-step-visual">
                  <div className="lv5-qr-mock">
                    <div className="lv5-qr-grid">
                      {qrPattern.map((v, i) => (
                        <span key={i} className={v ? '' : 'off'} />
                      ))}
                    </div>
                    <div className="lv5-qr-url">scan.revyo.fr</div>
                    <div className="lv5-qr-finger">👆</div>
                  </div>
                </div>
              </div>

              <div className="lv5-step lv5-stagger-child">
                <div className="lv5-step-num">{t('how.step_label')} 02</div>
                <div className="lv5-step-title">{t('how.step2_title')}</div>
                <div className="lv5-step-desc">{t('how.step2_desc')}</div>
                <div className="lv5-step-visual">
                  <div className="lv5-choice-mock">
                    <div className="lv5-choice-btn active"><span className="lv5-choice-ic">★</span>{t('how.step2_choice_excellent')}</div>
                    <div className="lv5-choice-btn"><span className="lv5-choice-ic">☺</span>{t('how.step2_choice_bien')}</div>
                    <div className="lv5-choice-btn"><span className="lv5-choice-ic">✎</span>{t('how.step2_choice_moyen')}</div>
                    <div className="lv5-choice-note">{t('how.step2_choice_note')}</div>
                  </div>
                </div>
              </div>

              <div className="lv5-step lv5-stagger-child">
                <div className="lv5-step-num">{t('how.step_label')} 03</div>
                <div className="lv5-step-title">{t('how.step3_title')}</div>
                <div className="lv5-step-desc">{t('how.step3_desc')}</div>
                <div className="lv5-step-visual">
                  <div className="lv5-rating-mock">
                    <div className="lv5-rating-label">{t('how.step3_note_label')}</div>
                    <div className="lv5-rating-value">4,6</div>
                    <div className="lv5-rating-stars"><span>★</span><span>★</span><span>★</span><span>★</span><span>★</span></div>
                    <div className="lv5-rating-delta">↑ +0,6</div>
                  </div>
                </div>
              </div>
            </div>

            <p className="lv5-how-tagline">
              <Trans i18nKey="landing:how.tagline" components={{ strong: <strong /> }} />
            </p>
          </section>

          {/* ═══ DASHBOARD SECTION — scroll-veil trigger ═══ */}
          <section className="lv5-dashboard-section lv5-reveal" id="dashboard">
            <div className="lv5-section-head">
              <div className="lv5-section-eyebrow">{t('dashboard.eyebrow')}</div>
              <h2>{t('dashboard.title_main')} <span className="lv5-dim">{t('dashboard.title_dim')}</span></h2>
              <p>{t('dashboard.subtitle')}</p>
            </div>

            <div className="lv5-dash-grid">
              <div className="lv5-dash-features">
                <div className="lv5-dash-feature lv5-stagger-child">
                  <div className="lv5-dash-icon">★</div>
                  <div>
                    <div className="lv5-dash-feat-title">{t('dashboard.feat1_title')}</div>
                    <div className="lv5-dash-feat-desc">{t('dashboard.feat1_desc')}</div>
                  </div>
                </div>
                <div className="lv5-dash-feature lv5-stagger-child">
                  <div className="lv5-dash-icon">⚡</div>
                  <div>
                    <div className="lv5-dash-feat-title">{t('dashboard.feat2_title')}</div>
                    <div className="lv5-dash-feat-desc">{t('dashboard.feat2_desc')}</div>
                  </div>
                </div>
                <div className="lv5-dash-feature lv5-stagger-child">
                  <div className="lv5-dash-icon">✦</div>
                  <div>
                    <div className="lv5-dash-feat-title">{t('dashboard.feat3_title')}</div>
                    <div className="lv5-dash-feat-desc">{t('dashboard.feat3_desc')}</div>
                  </div>
                </div>
                <div className="lv5-dash-feature lv5-stagger-child">
                  <div className="lv5-dash-icon">◐</div>
                  <div>
                    <div className="lv5-dash-feat-title">{t('dashboard.feat4_title')}</div>
                    <div className="lv5-dash-feat-desc">{t('dashboard.feat4_desc')}</div>
                  </div>
                </div>
              </div>

              <div className="lv5-dash-mock">
                <div className="lv5-dash-bar">
                  <div className="lv5-dash-url">app.revyo.fr/dashboard</div>
                  <div className="lv5-dash-user">
                    <span className="lv5-dash-user-name">{t('dashboard.mock_hello')}</span>
                    <div className="lv5-dash-avatar">X</div>
                  </div>
                </div>

                <div className="lv5-dash-kpis">
                  <div className="lv5-kpi">
                    <div className="lv5-kpi-label">{t('dashboard.kpi_note_label')}</div>
                    <div className="lv5-kpi-value with-star">{noteAnim}<span className="lv5-star">★</span></div>
                    <div className="lv5-kpi-trend">{t('dashboard.kpi_note_trend')}</div>
                  </div>
                  <div className="lv5-kpi">
                    <div className="lv5-kpi-label">{t('dashboard.kpi_avis_label')}</div>
                    <div className="lv5-kpi-value">{avisAnim}</div>
                    <div className="lv5-kpi-trend muted">{t('dashboard.kpi_avis_trend')}</div>
                  </div>
                  <div className="lv5-kpi">
                    <div className="lv5-kpi-label">{t('dashboard.kpi_satis_label')}</div>
                    <div className="lv5-kpi-value">{satisAnim}%</div>
                    <div className="lv5-kpi-trend muted">{t('dashboard.kpi_satis_trend')}</div>
                  </div>
                  <div className="lv5-kpi alert">
                    <div className="lv5-kpi-label">{t('dashboard.kpi_feedback_label')}</div>
                    <div className="lv5-kpi-value">3<span className="lv5-kpi-badge">{t('dashboard.kpi_feedback_badge')}</span></div>
                    <div className="lv5-kpi-trend muted">{t('dashboard.kpi_feedback_trend')}</div>
                  </div>
                </div>

                <div className="lv5-dash-chart">
                  <div className="lv5-dash-chart-head">
                    <div className="lv5-dash-chart-title">{t('dashboard.chart_title')}</div>
                    <div className="lv5-dash-chart-sub">{t('dashboard.chart_sub')}</div>
                  </div>
                  <svg className="lv5-chart-svg" viewBox="0 0 300 80" preserveAspectRatio="none">
                    <defs>
                      <linearGradient id="lv5cg" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#E8502A" stopOpacity=".5" />
                        <stop offset="100%" stopColor="#E8502A" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    <path d="M0,55 L40,50 L70,52 L100,45 L140,40 L170,35 L210,28 L240,22 L280,18 L300,15 L300,80 L0,80 Z" fill="url(#lv5cg)" />
                    <path ref={chartPathRef} className="lv5-chart-line" d="M0,55 L40,50 L70,52 L100,45 L140,40 L170,35 L210,28 L240,22 L280,18 L300,15" stroke="#E8502A" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                    <circle cx="300" cy="15" r="4" fill="#E8502A" />
                    <text x="6" y="68" fill="rgba(245,242,235,.4)" fontSize="8" fontFamily="Sora">4,0</text>
                    <text x="270" y="11" fill="#E8502A" fontSize="9" fontFamily="Sora" fontWeight="700">4,6</text>
                  </svg>
                </div>

                <div className="lv5-dash-insights">
                  <div className="lv5-dash-insights-head">{t('dashboard.insights_head')}</div>
                  <div className="lv5-dash-insight-item">{t('dashboard.insight1')} <small>{t('dashboard.insight1_cited')}</small></div>
                  <div className="lv5-dash-insight-item">{t('dashboard.insight2')} <small>{t('dashboard.insight2_cited')}</small></div>
                  <div className="lv5-dash-insight-item">{t('dashboard.insight3')} <small>{t('dashboard.insight3_cited')}</small></div>
                </div>
              </div>
            </div>
          </section>

          {/* ═══ PRICING ═══ */}
          <section className="lv5-pricing lv5-reveal" id="pricing">
            {/* Fade-up doux du head (2026-07-05) : eyebrow → titre → sous-titre
                cascadent via .lv5-stagger-child, puis les cards plans suivent
                (délais décalés dans le CSS .lv5-pricing.is-visible). */}
            <div className="lv5-section-head">
              <div className="lv5-section-eyebrow lv5-stagger-child">{t('pricing.eyebrow')}</div>
              <h2 className="lv5-stagger-child">{t('pricing.title_main')} <span className="lv5-dim">{t('pricing.title_dim')}</span></h2>
              <p className="lv5-stagger-child">{t('pricing.subtitle')}</p>
            </div>

            <div className="lv5-plans">
              <div
                className="lv5-plan lv5-stagger-child"
                role="button"
                tabIndex={0}
                onClick={() => goRegister('decouverte')}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goRegister('decouverte'); } }}
              >
                <div className="lv5-plan-eyebrow">{t('pricing.plan_decouverte.eyebrow')}</div>
                <div className="lv5-plan-name">{t('pricing.plan_decouverte.name')}</div>
                <div className="lv5-plan-price">{renderAmount(PLAN_BY_ID.decouverte.price)}<span className="lv5-period">{t('pricing.period')}</span></div>
                <button className="lv5-plan-cta ghost" onClick={(e) => { e.stopPropagation(); goRegister('decouverte'); }}>{t('pricing.plan_decouverte.cta')}</button>
                <ul className="lv5-plan-features">
                  {(t('pricing.plan_decouverte.features', { returnObjects: true }) || []).map((feat, i) => (
                    <li key={i}>{feat}</li>
                  ))}
                </ul>
              </div>

              <div
                className="lv5-plan featured lv5-stagger-child"
                role="button"
                tabIndex={0}
                onClick={() => goRegister('pro')}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goRegister('pro'); } }}
              >
                <div className="lv5-plan-tag">{t('pricing.plan_pro.tag')}</div>
                <div className="lv5-plan-eyebrow">{t('pricing.plan_pro.eyebrow')}</div>
                <div className="lv5-plan-name">{t('pricing.plan_pro.name')}</div>
                <div className="lv5-plan-price">{renderAmount(PLAN_BY_ID.pro.price)}<span className="lv5-period">{t('pricing.period')}</span></div>
                <button className="lv5-plan-cta solid" onClick={(e) => { e.stopPropagation(); goRegister('pro'); }}>{t('pricing.plan_pro.cta')}</button>
                <ul className="lv5-plan-features">
                  {(t('pricing.plan_pro.features', { returnObjects: true }) || []).map((feat, i) => (
                    <li key={i}>{feat}</li>
                  ))}
                </ul>
              </div>

              <div
                className="lv5-plan lv5-stagger-child"
                role="button"
                tabIndex={0}
                onClick={() => goRegister('intensif')}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goRegister('intensif'); } }}
              >
                <div className="lv5-plan-eyebrow">{t('pricing.plan_intensif.eyebrow')}</div>
                <div className="lv5-plan-name">{t('pricing.plan_intensif.name')}</div>
                <div className="lv5-plan-price">{renderAmount(PLAN_BY_ID.intensif.price)}<span className="lv5-period">{t('pricing.period')}</span></div>
                <button className="lv5-plan-cta ghost" onClick={(e) => { e.stopPropagation(); goRegister('intensif'); }}>{t('pricing.plan_intensif.cta')}</button>
                <ul className="lv5-plan-features">
                  {(t('pricing.plan_intensif.features', { returnObjects: true }) || []).map((feat, i) => (
                    <li key={i}>{feat}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="lv5-pricing-bottom">
              <span className="lv5-trust-item"><span className="lv5-check">✓</span>{t('common:trust.fourteen_days_free')}</span>
              <span className="lv5-trust-item"><span className="lv5-check">✓</span>{t('common:trust.immediate_access')}</span>
              <span className="lv5-trust-item"><span className="lv5-check">✓</span>{t('common:trust.cancel_one_click')}</span>
            </div>

            {/* Group block */}
            <div className="lv5-group">
              <div className="lv5-group-content">
                <div className="lv5-group-eyebrow">{t('pricing.group.eyebrow')}</div>
                <h3>{t('pricing.group.title')}</h3>
                <p>{t('pricing.group.text')}</p>
                <ul className="lv5-group-list">
                  {(t('pricing.group.list', { returnObjects: true }) || []).map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
                <a className="lv5-group-cta" href="mailto:contact@getrevyo.com?subject=Demande%20de%20devis%20groupe">
                  {t('pricing.group.cta')}
                </a>
              </div>
              <div className="lv5-group-visual">
                <div className="lv5-group-tiles">
                  <div className="lv5-gt"><div className="lv5-gt-disc">−15%</div><div className="lv5-gt-range">{t('pricing.group.tile_2_4_range')}</div></div>
                  <div className="lv5-gt"><div className="lv5-gt-disc">−25%</div><div className="lv5-gt-range">{t('pricing.group.tile_5_9_range')}</div></div>
                  <div className="lv5-gt"><div className="lv5-gt-disc">{t('pricing.group.tile_10_disc')}</div><div className="lv5-gt-range">{t('pricing.group.tile_10_range')}</div></div>
                  <div className="lv5-gt"><div className="lv5-gt-disc lv5-gt-bonus">{t('pricing.group.tile_bonus_disc')}</div><div className="lv5-gt-range">{t('pricing.group.tile_bonus_range')}</div></div>
                </div>
              </div>
            </div>
          </section>

          {/* ═══ FAQ ═══ */}
          <section className="lv5-faq lv5-reveal" id="faq">
            <div className="lv5-section-head">
              <div className="lv5-section-eyebrow">{t('faq.eyebrow')}</div>
              <h2>{t('faq.title_main')} <span className="lv5-dim">{t('faq.title_dim')}</span></h2>
              <p>{t('faq.subtitle')}</p>
            </div>

            <div className="lv5-faq-list">
              {faqList.map((item, i) => (
                <div key={i} className={'lv5-faq-item lv5-stagger-child' + (openFaq === i ? ' open' : '')}>
                  <button
                    className="lv5-faq-q"
                    type="button"
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  >
                    <span>{item.q}</span>
                    <span className="lv5-faq-icon">+</span>
                  </button>
                  <div className="lv5-faq-body">
                    <div className="lv5-faq-body-inner">
                      <Trans i18nKey={`landing:faq.items.${i}.a`} components={{ em: <em />, strong: <strong /> }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="lv5-faq-contact">
              <h4>{t('faq.contact.title')}</h4>
              <p>{t('faq.contact.text')}</p>
              <a href="mailto:contact@getrevyo.com">{t('faq.contact.link')}</a>
              <span className="lv5-faq-email">contact@getrevyo.com</span>
            </div>
          </section>

        </div>
      </div>

      <Footer />
    </>
  );
}

export const landingCss = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Sora:wght@600;700;800&display=swap');

/* ═══ Theme vars + body reset ═══ */
.lv5 {
  --lv5-orange: #E8502A;
  --lv5-orange-glow: #FF6B3D;
  --lv5-orange-deep: #C73E1D;
  --lv5-sand: #EDEAE3;
  --lv5-dark: #0A0908;
  --lv5-dark-2: #13110F;
  --lv5-line: rgba(237,234,227,.08);
  --lv5-line-2: rgba(237,234,227,.14);
  --lv5-text: #F5F2EB;
  --lv5-text-dim: rgba(245,242,235,.62);
  color: var(--lv5-text);
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  -webkit-font-smoothing: antialiased;
}
:root { --lv5-veil: 0; }
body:has(.lv5) { background: #0A0908; color: #F5F2EB; overflow-x: hidden; }

.lv5 *, .lv5 *::before, .lv5 *::after { box-sizing: border-box; margin: 0; padding: 0; }
.lv5 button { font-family: inherit; border: none; cursor: pointer; color: inherit; }
.lv5 ul { list-style: none; }
.lv5 a { color: inherit; text-decoration: none; }

/* ═══ Backgrounds — fixed below content ═══ */
.lv5-aurora {
  position: fixed; inset: -20%; z-index: 0; pointer-events: none;
  filter: blur(80px);
  opacity: calc(.65 * (1 - var(--lv5-veil, 0)));
  transition: opacity .15s linear;
}
.lv5-aurora::before, .lv5-aurora::after, .lv5-aurora span {
  content: ""; position: absolute; border-radius: 50%; mix-blend-mode: screen;
}
.lv5-aurora::before {
  width: 720px; height: 720px;
  background: radial-gradient(circle, #E8502A 0%, transparent 60%);
  top: -10%; left: -10%;
  animation: lv5-drift1 22s ease-in-out infinite alternate;
}
.lv5-aurora::after {
  width: 540px; height: 540px;
  background: radial-gradient(circle, #FF8A3D 0%, transparent 60%);
  bottom: -15%; right: -5%;
  animation: lv5-drift2 28s ease-in-out infinite alternate;
}
.lv5-aurora span {
  width: 420px; height: 420px;
  background: radial-gradient(circle, #C73E1D 0%, transparent 65%);
  top: 30%; left: 45%;
  animation: lv5-drift3 26s ease-in-out infinite alternate;
}
@keyframes lv5-drift1 { to { transform: translate(160px, 80px) scale(1.15); } }
@keyframes lv5-drift2 { to { transform: translate(-120px, -100px) scale(.9); } }
@keyframes lv5-drift3 { to { transform: translate(-200px, 140px) scale(1.1); } }

.lv5-orange-veil {
  position: fixed; inset: 0; z-index: 1; pointer-events: none;
  background: radial-gradient(ellipse at center, #E8502A 0%, #C73E1D 70%, #8C2A14 100%);
  opacity: var(--lv5-veil, 0);
  transition: opacity .12s linear;
}
.lv5-orange-veil::after {
  content: ""; position: absolute; inset: 0;
  background: radial-gradient(circle at 50% 50%, rgba(255,180,120,.4) 0%, transparent 50%);
  opacity: var(--lv5-veil, 0);
  mix-blend-mode: screen;
}

.lv5-grain {
  position: fixed; inset: 0; z-index: 2; pointer-events: none;
  opacity: .06; mix-blend-mode: overlay;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='.9'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>");
}

.lv5-wrap { position: relative; z-index: 5; max-width: 1240px; margin: 0 auto; padding: 88px 28px 0; }

/* ═══ NAV ═══ */
.lv5-nav {
  position: fixed; top: 0; left: 0; right: 0; z-index: 60;
  padding: 0 28px;
  background: transparent;
  border-bottom: 1px solid transparent;
  transition: background .3s ease, backdrop-filter .3s ease, border-color .3s ease, box-shadow .3s ease;
}
.lv5-nav.scrolled {
  background: rgba(10, 9, 8, .72);
  backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
  border-bottom-color: var(--lv5-line);
  box-shadow: 0 4px 24px rgba(0, 0, 0, .25);
}
.lv5-nav-inner {
  max-width: 1240px; margin: 0 auto;
  display: flex; align-items: center; justify-content: space-between;
  padding: 22px 0; gap: 24px;
}
.lv5-logo {
  display: flex; align-items: center; gap: 10px;
  font-weight: 700; font-size: 20px; letter-spacing: -.02em;
  color: var(--lv5-text); flex-shrink: 0; background: transparent;
}
.lv5-logo-mark {
  width: 34px; height: 34px; border-radius: 10px;
  background: linear-gradient(135deg, #E8502A, #C73E1D);
  display: flex; align-items: center; justify-content: center;
  color: white; font-weight: 800;
  box-shadow: 0 8px 24px rgba(232,80,42,.4);
}
.lv5-nav-pill {
  display: flex; gap: 4px; align-items: center;
  padding: 6px; border-radius: 100px;
  background: rgba(255,255,255,.04);
  border: 1px solid var(--lv5-line-2);
  backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
  box-shadow: 0 4px 16px rgba(0,0,0,.15), inset 0 1px 0 rgba(255,255,255,.04);
}
.lv5-nav-pill button, .lv5-nav-pill a {
  font-size: 14px; font-weight: 500;
  padding: 10px 20px; border-radius: 100px;
  transition: all .25s cubic-bezier(.2,.8,.2,1); background: transparent;
}
.lv5-nav-pill button:hover, .lv5-nav-pill a:hover {
  background: rgba(255,255,255,.06);
}
.lv5-nav-cta { display: flex; gap: 12px; align-items: center; flex-shrink: 0; }
.lv5-btn-ghost {
  font-size: 14px; font-weight: 500;
  padding: 10px 16px; border-radius: 10px;
  transition: all .2s; background: transparent;
}
.lv5-btn-ghost:hover { background: rgba(255,255,255,.05); }
.lv5-btn-primary {
  background: linear-gradient(135deg, #E8502A 0%, #FF6B3D 100%);
  color: white; padding: 11px 22px; border-radius: 11px;
  font-size: 14px; font-weight: 600;
  box-shadow: 0 10px 30px -8px rgba(232,80,42,.55), inset 0 1px 0 rgba(255,255,255,.2);
  transition: transform .2s, box-shadow .2s;
}
.lv5-btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 14px 40px -8px rgba(232,80,42,.7);
}
.lv5-btn-rps {
  color: #E8502A; font-size: 14px; font-weight: 600;
  padding: 10px 18px; border-radius: 11px;
  border: 1px solid rgba(232,80,42,.4);
  background: rgba(232,80,42,.06);
  backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
  transition: all .2s;
  display: inline-flex; align-items: center;
}
.lv5-btn-rps:hover {
  background: rgba(232,80,42,.12);
  border-color: rgba(232,80,42,.7);
  color: #FF8A3D;
}
.lv5-mobile-trigger {
  display: none; width: 44px; height: 44px; border-radius: 12px;
  background: rgba(255,255,255,.04); border: 1px solid var(--lv5-line-2);
  flex-direction: column; align-items: center; justify-content: center; gap: 5px;
  transition: background .2s;
}
.lv5-mobile-trigger:hover { background: rgba(255,255,255,.08); }
.lv5-mobile-trigger span {
  display: block; width: 18px; height: 1.5px; background: var(--lv5-text);
  border-radius: 2px; transition: all .3s cubic-bezier(.2,.8,.2,1);
}
.lv5-mobile-trigger.open span:nth-child(1) { transform: translateY(6.5px) rotate(45deg); }
.lv5-mobile-trigger.open span:nth-child(2) { opacity: 0; }
.lv5-mobile-trigger.open span:nth-child(3) { transform: translateY(-6.5px) rotate(-45deg); }

/* ═══ MOBILE MENU OVERLAY ═══ */
.lv5-mobile-menu {
  position: fixed; inset: 0; z-index: 55;
  background: rgba(10,9,8,.92);
  backdrop-filter: blur(24px); -webkit-backdrop-filter: blur(24px);
  display: flex; flex-direction: column; padding: 100px 28px 40px; gap: 8px;
  opacity: 0; pointer-events: none; transform: translateY(-12px);
  transition: opacity .3s, transform .3s cubic-bezier(.2,.8,.2,1);
}
.lv5-mobile-menu.open { opacity: 1; pointer-events: auto; transform: translateY(0); }
.lv5-mm-link {
  font-family: inherit; background: none; border: none; cursor: pointer;
  color: #F5F2EB; font-weight: 600; font-size: 24px;
  padding: 18px 0; border-bottom: 1px solid rgba(237,234,227,.08);
  letter-spacing: -.02em; text-decoration: none;
  display: flex; align-items: center; justify-content: space-between;
  text-align: left; width: 100%;
}
.lv5-mm-link:last-of-type { border-bottom: none; }
.lv5-mm-link span { opacity: .4; transition: opacity .2s; }
.lv5-mm-link:hover span { opacity: 1; color: #E8502A; }
.lv5-mm-cta {
  font-family: inherit; cursor: pointer; border: none;
  margin-top: 24px;
  background: linear-gradient(135deg, #E8502A, #FF6B3D);
  color: white; text-align: center;
  padding: 18px 24px; border-radius: 14px; font-size: 16px; font-weight: 600;
  box-shadow: 0 16px 40px -10px rgba(232,80,42,.6);
}
.lv5-mm-close {
  position: absolute; top: 18px; right: 22px;
  width: 44px; height: 44px;
  background: rgba(255,255,255,.04);
  border: 1px solid rgba(237,234,227,.14);
  border-radius: 12px;
  color: #F5F2EB; font-size: 32px; line-height: 1;
  font-family: inherit; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: background .15s, border-color .15s;
}
.lv5-mm-close:hover { background: rgba(255,255,255,.08); border-color: rgba(237,234,227,.28); }

/* ═══ HERO — 2 cols: text left / animated phone right ═══ */
.lv5-hero {
  padding: 48px 0 90px;
  position: relative;
  display: grid;
  grid-template-columns: 1.05fr .95fr;
  gap: 64px;
  align-items: center;
}
/* Fix 4 — Glow orange diffus sur le hero.
   --lv5-hero-glow (piloté scroll) × --lv5-hero-glow-in (fade-in 1.4s au load).
   Reduced-motion : opacity figée à .35 (cf @media en bas du fichier). */
.lv5-hero::before {
  content: "";
  position: absolute;
  inset: -10% -10% -20% -10%;
  background: radial-gradient(
    ellipse 80% 60% at 50% 35%,
    rgba(232, 80, 42, .22) 0%,
    rgba(232, 80, 42, .08) 35%,
    transparent 70%
  );
  filter: blur(60px);
  opacity: calc(var(--lv5-hero-glow, 1) * var(--lv5-hero-glow-in, 1));
  pointer-events: none;
  z-index: -1;
  transition: opacity .15s linear;
}
.lv5-hero-text, .lv5-hero-visual { position: relative; z-index: 1; }
.lv5-hero-text {
  text-align: left;
  animation: lv5-fadeUp .8s ease-out;
  min-width: 0;
}
.lv5-hero-visual {
  position: relative;
  display: flex; justify-content: center; align-items: center;
  min-height: 680px;
  animation: lv5-fadeUp .9s .15s ease-out backwards;
}
.lv5-h1 {
  font-family: 'Inter', sans-serif; font-weight: 700;
  font-size: clamp(40px, 5.2vw, 68px); line-height: 1.04;
  letter-spacing: -.02em; margin-bottom: 22px;
  animation: lv5-fadeUp .8s ease-out;
}
.lv5-accent {
  background: linear-gradient(135deg, #E8502A 0%, #FF8A3D 50%, #E8502A 100%);
  background-size: 200% 100%;
  -webkit-background-clip: text; background-clip: text; color: transparent;
  animation: lv5-shimmer 6s ease-in-out infinite;
}
@keyframes lv5-shimmer {
  0%,100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}
.lv5-sub {
  font-size: 18px; line-height: 1.55; color: var(--lv5-text-dim);
  max-width: 540px; margin: 0 0 30px;
  animation: lv5-fadeUp .8s .1s ease-out backwards;
}
.lv5-sub strong { color: var(--lv5-text); font-weight: 500; }

.lv5-cta-row {
  display: inline-flex; gap: 14px; align-items: center;
  flex-wrap: wrap; justify-content: flex-start; margin-bottom: 20px;
  animation: lv5-fadeUp .8s .2s ease-out backwards;
}
.lv5-cta-main {
  background: linear-gradient(135deg, #E8502A 0%, #FF6B3D 100%);
  color: white; padding: 15px 26px; border-radius: 14px;
  font-size: 15px; font-weight: 600;
  display: inline-flex; align-items: center; gap: 10px;
  box-shadow: 0 16px 40px -10px rgba(232,80,42,.6), inset 0 1px 0 rgba(255,255,255,.25);
  transition: transform .25s cubic-bezier(.2,.8,.2,1), box-shadow .25s;
  position: relative; overflow: hidden;
}
.lv5-cta-main::before {
  content: ""; position: absolute; inset: 0;
  background: linear-gradient(120deg, transparent 30%, rgba(255,255,255,.25), transparent 70%);
  transform: translateX(-100%); transition: transform .6s;
}
.lv5-cta-main:hover {
  transform: translateY(-2px);
  box-shadow: 0 20px 50px -10px rgba(232,80,42,.75);
}
.lv5-cta-main:hover::before { transform: translateX(100%); }
.lv5-cta-secondary {
  color: var(--lv5-text); font-size: 15px; font-weight: 600;
  display: inline-flex; align-items: center; gap: 10px;
  padding: 15px 22px; border: 1px solid var(--lv5-line-2); border-radius: 14px;
  background: rgba(255,255,255,.03);
  backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
  transition: all .2s;
}
.lv5-cta-secondary:hover {
  background: rgba(255,255,255,.07);
  border-color: rgba(232,80,42,.4);
}

.lv5-trust {
  display: flex; gap: 22px; flex-wrap: wrap; justify-content: flex-start;
  animation: lv5-fadeUp .8s .3s ease-out backwards;
}
.lv5-trust-item {
  display: flex; align-items: center; gap: 7px;
  font-size: 13px; color: var(--lv5-text-dim);
}
.lv5-check { color: #E8502A; font-weight: 700; }

/* ═══ Carte RPS (lead magnet) ═══ */
.lv5-rps-card {
  display: flex; align-items: center; gap: 16px;
  max-width: 520px; margin: 28px 0 0;
  padding: 18px 22px;
  background: linear-gradient(135deg, rgba(232,80,42,.08) 0%, rgba(255,255,255,.025) 100%);
  border: 1px solid var(--lv5-line-2); border-radius: 18px;
  backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
  color: var(--lv5-text);
  transition: all .25s cubic-bezier(.2,.8,.2,1);
  animation: lv5-fadeUp .8s .35s ease-out backwards;
  position: relative; overflow: hidden;
}
.lv5-rps-card::before {
  content: ""; position: absolute; inset: 0;
  background: radial-gradient(circle at 0% 50%, rgba(232,80,42,.15) 0%, transparent 60%);
  opacity: 0; transition: opacity .3s;
}
.lv5-rps-card:hover {
  transform: translateY(-2px);
  border-color: rgba(232,80,42,.4);
  box-shadow: 0 16px 40px -12px rgba(232,80,42,.3);
}
.lv5-rps-card:hover::before { opacity: 1; }
.lv5-rps-icon {
  width: 46px; height: 46px; border-radius: 12px;
  background: rgba(232,80,42,.15); border: 1px solid rgba(232,80,42,.25);
  display: flex; align-items: center; justify-content: center;
  font-size: 22px; flex-shrink: 0; position: relative;
}
.lv5-rps-text { flex: 1; min-width: 0; text-align: left; position: relative; }
.lv5-rps-title {
  font-size: 15px; font-weight: 700; color: var(--lv5-text);
  margin-bottom: 3px; line-height: 1.3;
}
.lv5-rps-accent { color: #E8502A; }
.lv5-rps-sub { font-size: 13px; color: var(--lv5-text-dim); line-height: 1.3; }
.lv5-rps-arrow {
  color: #E8502A; font-weight: 700; font-size: 22px;
  flex-shrink: 0; position: relative; transition: transform .25s;
}
.lv5-rps-card:hover .lv5-rps-arrow { transform: translateX(4px); }

/* ═══ PROBLEM ═══ */
.lv5-problem { padding: 140px 0 100px; position: relative; z-index: 5; }
.lv5-problem-intro { text-align: center; max-width: 880px; margin: 0 auto 70px; }
.lv5-problem-h2 {
  font-family: 'Sora', 'Inter', sans-serif; font-weight: 700;
  font-size: clamp(32px, 4.5vw, 52px); line-height: 1.08;
  letter-spacing: -.035em; margin-bottom: 18px; text-wrap: balance;
}
.lv5-problem-sub {
  font-size: 19px; color: var(--lv5-text-dim);
  line-height: 1.5; text-wrap: balance;
}
.lv5-problem-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 22px; }
.lv5-prob-card {
  padding: 32px 28px; border: 1px solid var(--lv5-line); border-radius: 22px;
  background: rgba(255,255,255,.025);
  backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
  display: flex; flex-direction: column; gap: 14px;
  transition: transform .35s cubic-bezier(.2,.8,.2,1), border-color .3s;
}
.lv5-prob-card:hover { transform: translateY(-4px); border-color: rgba(232,80,42,.25); }
.lv5-prob-ic {
  width: 50px; height: 50px; border-radius: 14px;
  background: rgba(232,80,42,.12); border: 1px solid rgba(232,80,42,.2);
  display: flex; align-items: center; justify-content: center;
  color: #E8502A; flex-shrink: 0;
}
.lv5-prob-ic.warn { background: rgba(255,193,61,.1); border-color: rgba(255,193,61,.25); color: #FFC93D; }
.lv5-prob-ic.down { background: rgba(248,113,113,.1); border-color: rgba(248,113,113,.25); color: #F87171; }
.lv5-prob-title {
  font-family: 'Sora', 'Inter', sans-serif; font-weight: 700; font-size: 20px;
  letter-spacing: -.015em; color: var(--lv5-text); line-height: 1.2;
}
.lv5-prob-text { font-size: 14.5px; line-height: 1.6; color: var(--lv5-text-dim); }
.lv5-prob-text em { color: var(--lv5-text); font-style: normal; font-weight: 500; }
.lv5-problem-bridge { margin-top: 70px; text-align: center; }
.lv5-problem-bridge-text {
  font-family: 'Sora', 'Inter', sans-serif; font-weight: 700;
  font-size: clamp(24px, 3vw, 34px);
  background: linear-gradient(135deg, #E8502A, #FF8A3D);
  -webkit-background-clip: text; background-clip: text; color: transparent;
  text-wrap: balance; letter-spacing: -.02em;
}

/* Problem sous veil orange (2026-07-05) — même traitement lisibilité que
   dashboard/FAQ : textes blancs, cards en verre assombri, icônes unifiées en
   blanc, et le bridge en dégradé orange passe en blanc (sinon invisible sur
   le fond orange). */
.lv5-problem .lv5-problem-h2,
.lv5-problem .lv5-problem-sub,
.lv5-problem .lv5-prob-card,
.lv5-problem .lv5-prob-ic,
.lv5-problem .lv5-prob-title,
.lv5-problem .lv5-prob-text,
.lv5-problem .lv5-prob-text em {
  transition: color .3s ease, background .3s ease, border-color .3s ease;
}
body.lv5-veil-high .lv5-problem .lv5-problem-h2 { color: #FFFFFF; text-shadow: 0 2px 12px rgba(0,0,0,.18); }
body.lv5-veil-high .lv5-problem .lv5-problem-sub { color: rgba(255,255,255,.92); }
body.lv5-veil-high .lv5-problem .lv5-prob-card {
  background: rgba(0,0,0,.18); border-color: rgba(255,255,255,.22);
  backdrop-filter: blur(20px);
}
body.lv5-veil-high .lv5-problem .lv5-prob-card:hover { border-color: rgba(255,255,255,.4); }
body.lv5-veil-high .lv5-problem .lv5-prob-ic {
  background: rgba(255,255,255,.22); border-color: rgba(255,255,255,.35); color: #FFFFFF;
}
body.lv5-veil-high .lv5-problem .lv5-prob-title { color: #FFFFFF; text-shadow: 0 1px 6px rgba(0,0,0,.15); }
body.lv5-veil-high .lv5-problem .lv5-prob-text { color: rgba(255,255,255,.92); }
body.lv5-veil-high .lv5-problem .lv5-prob-text em { color: #FFFFFF; }
body.lv5-veil-high .lv5-problem .lv5-problem-bridge-text {
  background: none; -webkit-background-clip: initial; background-clip: initial;
  color: #FFFFFF; text-shadow: 0 2px 12px rgba(0,0,0,.2);
}

/* ═══ PHONE (in hero) ═══ */
.lv5-phone-stage {
  position: relative; display: flex; justify-content: center; align-items: center;
}
.lv5-phone {
  width: 320px; height: 640px;
  background: linear-gradient(160deg, #1a1715, #0a0908);
  border-radius: 48px; padding: 14px; position: relative;
  border: 1px solid rgba(255,255,255,.08);
  box-shadow:
    0 80px 160px -30px rgba(0,0,0,.7),
    0 30px 60px -20px rgba(0,0,0,.5),
    inset 0 1px 0 rgba(255,255,255,.08);
  animation: lv5-float 6s ease-in-out infinite;
}
@keyframes lv5-float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-12px); }
}
.lv5-phone-screen {
  width: 100%; height: 100%;
  background: #F5F2EB; border-radius: 36px;
  overflow: hidden; position: relative;
}
.lv5-phone-notch {
  position: absolute; top: 14px; left: 50%; transform: translateX(-50%);
  width: 110px; height: 28px; background: #0a0908; border-radius: 20px; z-index: 5;
}

/* Scene cycler */
.lv5-scene {
  position: absolute; inset: 0;
  display: flex; flex-direction: column;
  background: #F5F2EB;
  opacity: 0; transition: opacity .55s ease;
  pointer-events: none;
}
.lv5-scene.is-active { opacity: 1; pointer-events: auto; }

/* Scene 1 — QR */
.lv5-qr-scene {
  flex: 1; display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  padding: 0 24px; background: #F5F2EB;
  position: relative; text-align: center; gap: 14px;
}
.lv5-qr-scene-eyebrow {
  font-size: 10px; font-weight: 700; letter-spacing: .22em;
  color: #E8502A; text-transform: uppercase;
  display: flex; align-items: center; gap: 8px;
}
.lv5-qr-scene-eyebrow::before, .lv5-qr-scene-eyebrow::after {
  content: ""; width: 18px; height: 1px;
  background: linear-gradient(90deg, transparent, #E8502A);
}
.lv5-qr-scene-eyebrow::after {
  background: linear-gradient(90deg, #E8502A, transparent);
}
.lv5-qr-wrap {
  position: relative; width: 186px; height: 186px; border-radius: 20px; padding: 18px;
  background: white;
  box-shadow:
    0 0 0 1px rgba(232,80,42,.25),
    0 0 40px rgba(232,80,42,.4),
    0 18px 40px -8px rgba(232,80,42,.45),
    inset 0 0 0 1px rgba(255,255,255,.5);
  overflow: hidden;
  animation: lv5-qrGlow 2.8s ease-in-out infinite;
}
@keyframes lv5-qrGlow {
  0%, 100% {
    box-shadow:
      0 0 0 1px rgba(232,80,42,.25),
      0 0 40px rgba(232,80,42,.4),
      0 18px 40px -8px rgba(232,80,42,.45),
      inset 0 0 0 1px rgba(255,255,255,.5);
  }
  50% {
    box-shadow:
      0 0 0 1px rgba(232,80,42,.5),
      0 0 60px rgba(232,80,42,.65),
      0 18px 50px -8px rgba(232,80,42,.6),
      inset 0 0 0 1px rgba(255,255,255,.5);
  }
}
.lv5-qr-scene-big {
  position: relative; width: 100%; height: 100%;
  display: grid;
  grid-template-columns: repeat(8, 1fr); grid-template-rows: repeat(8, 1fr);
  gap: 2px; z-index: 1;
}
.lv5-qr-scene-big span { background: #0a0908; border-radius: 2px; }
.lv5-qr-scene-big span.off { background: transparent; }
.lv5-qr-scan-line {
  position: absolute; left: 6px; right: 6px; height: 2px; top: 0; z-index: 3;
  pointer-events: none;
  background: linear-gradient(90deg, transparent, #FF8A3D 20%, #FF8A3D 80%, transparent);
  box-shadow: 0 0 14px 2px rgba(255,138,61,.85), 0 0 28px 4px rgba(255,138,61,.4);
  animation: lv5-qrScan 1.8s cubic-bezier(.4,0,.6,1) infinite;
}
@keyframes lv5-qrScan {
  0% { top: 6px; opacity: 0; }
  8%, 92% { opacity: 1; }
  100% { top: calc(100% - 8px); opacity: 0; }
}
.lv5-qrc {
  position: absolute; width: 18px; height: 18px;
  border: 2px solid #E8502A; z-index: 4; pointer-events: none;
  filter: drop-shadow(0 0 4px rgba(232,80,42,.6));
}
.lv5-qrc-tl { top: 5px; left: 5px; border-right: none; border-bottom: none; border-radius: 6px 0 0 0; }
.lv5-qrc-tr { top: 5px; right: 5px; border-left: none; border-bottom: none; border-radius: 0 6px 0 0; }
.lv5-qrc-bl { bottom: 5px; left: 5px; border-right: none; border-top: none; border-radius: 0 0 0 6px; }
.lv5-qrc-br { bottom: 5px; right: 5px; border-left: none; border-top: none; border-radius: 0 0 6px 0; }
.lv5-qr-scene-cta { font-size: 14px; font-weight: 600; color: #0a0908; letter-spacing: -.01em; margin-top: 6px; }
.lv5-qr-scene-url { font-size: 11px; color: #E8502A; letter-spacing: .05em; font-weight: 600; }

/* Flash blanc style iPhone */
.lv5-qr-flash {
  position: absolute; inset: 0; background: #FFFFFF;
  opacity: 0; pointer-events: none; z-index: 50;
  box-shadow: inset 0 0 80px rgba(255,255,255,1);
}
.lv5-scene-qr.is-active .lv5-qr-flash {
  animation: lv5-qrFlash 2.8s linear forwards;
}
@keyframes lv5-qrFlash {
  0%, 86% { opacity: 0; }
  87% { opacity: 1; }
  88% { opacity: .9; }
  90% { opacity: .55; }
  93% { opacity: .22; }
  96% { opacity: .06; }
  100% { opacity: 0; }
}

/* Scene 2 — Satisfaction */
.lv5-scene-satisfaction.is-active .lv5-opt-excellent {
  animation: lv5-optClick 2.6s ease-out forwards;
}
@keyframes lv5-optClick {
  0%, 55% { border-color: rgba(10,9,8,.08); background: white; transform: translateX(0); }
  65%, 80% {
    border-color: #E8502A; background: rgba(232,80,42,.06);
    transform: translateX(2px) scale(1.015);
    box-shadow: 0 8px 20px -6px rgba(232,80,42,.3);
  }
  100% {
    border-color: #E8502A; background: rgba(232,80,42,.06);
    transform: translateX(2px) scale(1.015);
    box-shadow: 0 8px 20px -6px rgba(232,80,42,.3);
  }
}
.lv5-scene-satisfaction.is-active .lv5-opt-excellent::after {
  content: ""; position: absolute; width: 14px; height: 14px;
  border-radius: 50%; background: rgba(232,80,42,.45);
  right: 18px; top: 50%; transform: translateY(-50%) scale(0);
  animation: lv5-tapRipple 2.6s ease-out forwards;
  pointer-events: none;
}
@keyframes lv5-tapRipple {
  0%, 55% { transform: translateY(-50%) scale(0); opacity: 0; }
  60% { transform: translateY(-50%) scale(.4); opacity: .6; }
  80%, 100% { transform: translateY(-50%) scale(3.2); opacity: 0; }
}
.lv5-opt-excellent { position: relative; overflow: visible; }

.lv5-resto-head {
  padding: 50px 24px 18px;
  display: flex; align-items: center; gap: 11px;
  background: #F5F2EB;
}
.lv5-resto-logo {
  width: 40px; height: 40px; border-radius: 11px;
  background: linear-gradient(135deg, #E8502A, #C73E1D);
  display: flex; align-items: center; justify-content: center;
  color: white; font-weight: 700; font-size: 14px;
}
.lv5-resto-name { font-weight: 600; font-size: 14px; color: #0a0908; }
.lv5-resto-loc { font-size: 11px; color: rgba(10,9,8,.55); }
.lv5-resto-q { padding: 10px 24px 6px; background: #F5F2EB; }
.lv5-resto-q h3 {
  font-weight: 700; font-size: 19px; color: #0a0908;
  line-height: 1.22; margin-bottom: 4px; letter-spacing: -.02em;
}
.lv5-resto-q p { font-size: 12px; color: rgba(10,9,8,.55); }
.lv5-options {
  padding: 16px 24px; display: flex; flex-direction: column; gap: 9px;
  background: #F5F2EB; flex: 1;
}
.lv5-opt {
  padding: 12px 14px; border: 1px solid rgba(10,9,8,.08); border-radius: 14px;
  display: flex; align-items: center; gap: 12px;
  background: white; transition: all .2s;
}
.lv5-opt-emoji {
  width: 32px; height: 32px; border-radius: 9px;
  background: rgba(232,80,42,.08);
  display: flex; align-items: center; justify-content: center; font-size: 15px;
}
.lv5-opt-text { flex: 1; text-align: left; }
.lv5-opt-title { font-size: 13px; font-weight: 600; color: #0a0908; line-height: 1.1; }
.lv5-opt-sub { font-size: 11px; color: rgba(10,9,8,.5); margin-top: 1px; }
.lv5-opt-arrow { color: rgba(10,9,8,.3); }
.lv5-powered {
  padding: 11px 24px; text-align: center;
  font-size: 10px; color: rgba(10,9,8,.4); background: #F5F2EB;
}
.lv5-powered b { color: #E8502A; }

/* Scene 3 — Google */
.lv5-google-scene {
  flex: 1; display: flex; flex-direction: column; align-items: center;
  padding: 42px 22px 14px; background: #F5F2EB;
  text-align: center; gap: 10px;
}
.lv5-google-scene-emoji {
  font-size: 46px; line-height: 1;
  animation: lv5-starPop .8s cubic-bezier(.2,.8,.2,1.4) backwards;
  animation-delay: .15s;
}
@keyframes lv5-starPop {
  from { opacity: 0; transform: scale(.3) rotate(-20deg); }
  to { opacity: 1; transform: scale(1) rotate(0); }
}
.lv5-google-scene-title { font-size: 22px; font-weight: 700; color: #0a0908; letter-spacing: -.02em; }
.lv5-google-scene-sub { font-size: 12px; color: rgba(10,9,8,.55); line-height: 1.4; max-width: 240px; }
.lv5-google-block {
  display: flex; align-items: center; gap: 11px;
  padding: 11px 14px; border: 1px solid rgba(10,9,8,.08); border-radius: 13px;
  background: white; margin-top: 6px; width: 100%; text-align: left;
}
.lv5-google-icon {
  width: 34px; height: 34px; border-radius: 10px;
  background: white; border: 1px solid rgba(10,9,8,.1);
  display: flex; align-items: center; justify-content: center;
  font-weight: 700; font-size: 16px; color: #4285F4;
  font-family: 'Inter'; flex-shrink: 0;
}
.lv5-google-info { display: flex; flex-direction: column; gap: 1px; min-width: 0; flex: 1; }
.lv5-google-info strong { font-size: 12px; color: #0a0908; font-weight: 600; }
.lv5-google-info span { font-size: 10px; color: rgba(10,9,8,.5); }
.lv5-google-cta {
  width: 100%; padding: 13px 16px; border-radius: 12px; border: none;
  background: linear-gradient(135deg, #E8502A, #FF6B3D);
  color: white; font-size: 13px; font-weight: 700;
  letter-spacing: -.01em; cursor: pointer;
  box-shadow: 0 10px 24px -8px rgba(232,80,42,.55);
  margin-top: 4px; font-family: inherit;
}
.lv5-google-redirect { font-size: 10px; color: rgba(10,9,8,.5); margin-top: 2px; }
.lv5-progress-dots { display: flex; gap: 6px; margin-top: 10px; }
.lv5-pd { width: 6px; height: 6px; border-radius: 50%; background: rgba(10,9,8,.15); }
.lv5-pd.done { background: rgba(232,80,42,.4); }
.lv5-pd.active { background: #E8502A; box-shadow: 0 0 0 3px rgba(232,80,42,.18); }

/* Float cards */
.lv5-float-card {
  position: absolute;
  background: rgba(10,9,8,.55);
  backdrop-filter: blur(24px); -webkit-backdrop-filter: blur(24px);
  border: 1px solid rgba(255,255,255,.12);
  border-radius: 16px; padding: 13px 18px;
  display: flex; align-items: center; gap: 11px;
  font-size: 12px; color: var(--lv5-text);
  box-shadow: 0 20px 40px -12px rgba(0,0,0,.5);
}
.lv5-float-tr { top: 20px; right: calc(50% - 210px); animation: lv5-floatA 7s ease-in-out infinite; }
.lv5-float-bl { bottom: 30px; left: calc(50% - 220px); animation: lv5-floatB 8s ease-in-out infinite; }
@keyframes lv5-floatA { 0%, 100% { transform: translate(0, 0); } 50% { transform: translate(-6px, -10px); } }
@keyframes lv5-floatB { 0%, 100% { transform: translate(0, 0); } 50% { transform: translate(8px, -12px); } }
.lv5-float-icon {
  width: 34px; height: 34px; border-radius: 9px;
  display: flex; align-items: center; justify-content: center;
  font-weight: 700; font-size: 15px;
}
.lv5-icon-star { background: linear-gradient(135deg, #FFC93D, #FF8A3D); color: white; }
.lv5-icon-g { background: white; color: #0a0908; }
.lv5-float-label {
  font-size: 10px; color: rgba(245,242,235,.7);
  text-transform: uppercase; letter-spacing: .05em;
}
.lv5-float-value { font-weight: 700; font-size: 16px; letter-spacing: -.02em; }
.lv5-float-delta { color: #4ADE80; font-size: 11px; font-weight: 600; margin-left: 4px; }

/* ═══ Section heads communs ═══ */
.lv5-section-eyebrow {
  display: inline-block; font-size: 12px;
  text-transform: uppercase; letter-spacing: .18em;
  font-weight: 600; color: #E8502A; margin-bottom: 18px;
}
.lv5-section-head { text-align: center; max-width: 760px; margin: 0 auto 70px; }
.lv5-section-head h2 {
  font-family: 'Sora', 'Inter', sans-serif; font-weight: 700;
  font-size: clamp(34px, 5vw, 60px); line-height: 1.05;
  letter-spacing: -.035em; margin-bottom: 18px;
}
.lv5-dim { color: var(--lv5-text-dim); }
.lv5-section-head p {
  font-size: 17px; line-height: 1.55;
  color: var(--lv5-text-dim); max-width: 560px; margin: 0 auto;
}

/* ═══ HOW IT WORKS ═══ */
.lv5-how { padding: 120px 0; position: relative; z-index: 5; }
.lv5-steps { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; position: relative; }
.lv5-step {
  padding: 36px 28px; border: 1px solid var(--lv5-line); border-radius: 24px;
  background: rgba(255,255,255,.025);
  backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
  position: relative; overflow: hidden; display: flex; flex-direction: column;
  transition: transform .35s cubic-bezier(.2,.8,.2,1), border-color .3s;
}
.lv5-step:hover { transform: translateY(-4px); border-color: rgba(232,80,42,.25); }
.lv5-step-num {
  font-family: 'Sora', 'Inter', sans-serif; font-weight: 700; font-size: 13px;
  letter-spacing: .04em; color: #E8502A; margin-bottom: 20px;
  display: flex; align-items: center; gap: 10px;
}
.lv5-step-num::before {
  content: ""; width: 6px; height: 6px; border-radius: 50%;
  background: #E8502A; box-shadow: 0 0 0 4px rgba(232,80,42,.18);
}
.lv5-step-title {
  font-family: 'Sora', 'Inter', sans-serif; font-weight: 700;
  font-size: 22px; letter-spacing: -.02em; line-height: 1.2; margin-bottom: 14px;
}
.lv5-step-desc { font-size: 14px; line-height: 1.55; color: var(--lv5-text-dim); margin-bottom: 24px; flex: 1; }
.lv5-step-visual {
  margin-top: auto; border-radius: 16px;
  border: 1px solid var(--lv5-line); background: rgba(0,0,0,.25);
  padding: 18px; min-height: 140px;
  display: flex; align-items: center; justify-content: center;
  position: relative; overflow: hidden;
}
.lv5-qr-mock { display: flex; flex-direction: column; align-items: center; gap: 10px; position: relative; }
.lv5-qr-grid {
  width: 88px; height: 88px; background: white; border-radius: 10px; padding: 8px;
  display: grid; grid-template-columns: repeat(8, 1fr); grid-template-rows: repeat(8, 1fr); gap: 1px;
}
.lv5-qr-grid span { background: #0a0908; border-radius: 1px; }
.lv5-qr-grid span.off { background: transparent; }
.lv5-qr-url { font-family: 'Sora'; font-size: 11px; color: var(--lv5-text-dim); }
.lv5-qr-finger {
  position: absolute; top: -10px; right: -18px; font-size: 28px;
  animation: lv5-tap 2s ease-in-out infinite;
}
@keyframes lv5-tap {
  0%, 100% { transform: translate(0, 0) rotate(-15deg); }
  50% { transform: translate(-6px, -6px) rotate(-25deg); }
}
.lv5-choice-mock { display: flex; flex-direction: column; gap: 6px; width: 100%; }
.lv5-choice-btn {
  padding: 8px 12px; border: 1px solid var(--lv5-line); border-radius: 10px;
  display: flex; align-items: center; gap: 10px;
  font-size: 12px; background: rgba(0,0,0,.3);
}
.lv5-choice-ic {
  width: 22px; height: 22px; border-radius: 6px;
  background: rgba(232,80,42,.15);
  display: flex; align-items: center; justify-content: center;
  color: #E8502A; font-size: 11px;
}
.lv5-choice-btn.active { border-color: #E8502A; background: rgba(232,80,42,.08); }
.lv5-choice-note { margin-top: 6px; font-size: 10px; color: var(--lv5-text-dim); text-align: center; }
.lv5-rating-mock { display: flex; flex-direction: column; align-items: center; gap: 8px; }
.lv5-rating-label {
  font-size: 10px; text-transform: uppercase; letter-spacing: .1em;
  color: var(--lv5-text-dim);
}
.lv5-rating-value {
  font-family: 'Sora'; font-weight: 700; font-size: 36px;
  letter-spacing: -.03em; line-height: 1;
}
.lv5-rating-delta {
  display: inline-flex; align-items: center; gap: 4px;
  background: rgba(74,222,128,.12); color: #4ADE80;
  padding: 3px 9px; border-radius: 100px;
  font-size: 11px; font-weight: 600;
}
.lv5-rating-stars { display: flex; gap: 2px; }
.lv5-rating-stars span { color: #FFC93D; font-size: 14px; }
.lv5-how-tagline {
  text-align: center; margin-top: 60px;
  font-size: 17px; color: var(--lv5-text-dim); line-height: 1.5;
  max-width: 680px; margin-left: auto; margin-right: auto;
}
.lv5-how-tagline strong { color: var(--lv5-text); }

/* ═══ DASHBOARD SECTION ═══ */
.lv5-dashboard-section { padding: 120px 0; position: relative; z-index: 5; }
.lv5-dashboard-section .lv5-section-eyebrow,
.lv5-dashboard-section .lv5-section-head h2,
.lv5-dashboard-section .lv5-section-head h2 .lv5-dim,
.lv5-dashboard-section .lv5-section-head p,
.lv5-dashboard-section .lv5-dash-feat-title,
.lv5-dashboard-section .lv5-dash-feat-desc,
.lv5-dashboard-section .lv5-dash-icon,
.lv5-dashboard-section .lv5-dash-feature {
  transition: color .3s ease, background .3s ease, border-color .3s ease;
}
body.lv5-veil-high .lv5-dashboard-section .lv5-section-eyebrow { color: #FFFFFF; text-shadow: 0 1px 8px rgba(0,0,0,.2); }
body.lv5-veil-high .lv5-dashboard-section .lv5-section-head h2 { color: #FFFFFF; text-shadow: 0 2px 12px rgba(0,0,0,.18); }
body.lv5-veil-high .lv5-dashboard-section .lv5-section-head h2 .lv5-dim { color: rgba(255,255,255,.78); }
body.lv5-veil-high .lv5-dashboard-section .lv5-section-head p { color: rgba(255,255,255,.92); }
body.lv5-veil-high .lv5-dashboard-section .lv5-dash-feat-title { color: #FFFFFF; text-shadow: 0 1px 6px rgba(0,0,0,.15); }
body.lv5-veil-high .lv5-dashboard-section .lv5-dash-feat-desc { color: rgba(255,255,255,.92); }
body.lv5-veil-high .lv5-dashboard-section .lv5-dash-feature {
  background: rgba(0,0,0,.18); border-color: rgba(255,255,255,.22);
  backdrop-filter: blur(20px);
}
body.lv5-veil-high .lv5-dashboard-section .lv5-dash-icon {
  background: rgba(255,255,255,.22); color: #FFFFFF;
  border: 1px solid rgba(255,255,255,.35);
}
.lv5-dash-grid { display: grid; grid-template-columns: 1fr 1.15fr; gap: 60px; align-items: center; }
.lv5-dash-features { display: flex; flex-direction: column; gap: 24px; }
.lv5-dash-feature {
  padding: 24px; border: 1px solid var(--lv5-line); border-radius: 18px;
  background: rgba(255,255,255,.025);
  backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
  display: flex; gap: 16px; transition: all .25s;
}
.lv5-dash-feature:hover { border-color: rgba(232,80,42,.25); transform: translateX(4px); }
.lv5-dash-icon {
  width: 42px; height: 42px; border-radius: 11px;
  background: rgba(232,80,42,.15);
  display: flex; align-items: center; justify-content: center;
  color: #E8502A; flex-shrink: 0; font-size: 18px;
}
.lv5-dash-feat-title { font-weight: 700; font-size: 16px; letter-spacing: -.01em; margin-bottom: 6px; }
.lv5-dash-feat-desc { font-size: 13px; line-height: 1.5; color: var(--lv5-text-dim); }

.lv5-dash-mock {
  background: linear-gradient(160deg, #13110F, #0a0908);
  border: 1px solid var(--lv5-line-2); border-radius: 22px; padding: 22px;
  box-shadow: 0 50px 100px -20px rgba(0,0,0,.6), inset 0 1px 0 rgba(255,255,255,.04);
}
.lv5-dash-bar {
  display: flex; align-items: center; justify-content: space-between;
  padding-bottom: 18px; border-bottom: 1px solid var(--lv5-line);
  margin-bottom: 22px;
}
.lv5-dash-url { display: flex; align-items: center; gap: 8px; font-size: 11px; color: var(--lv5-text-dim); }
.lv5-dash-url::before { content: ""; width: 6px; height: 6px; border-radius: 50%; background: #4ADE80; }
.lv5-dash-user { display: flex; align-items: center; gap: 8px; font-size: 12px; }
.lv5-dash-user-name { color: var(--lv5-text-dim); }
.lv5-dash-avatar {
  width: 26px; height: 26px; border-radius: 50%;
  background: linear-gradient(135deg, #E8502A, #C73E1D);
  display: flex; align-items: center; justify-content: center;
  font-weight: 700; font-size: 11px;
}
.lv5-dash-kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 22px; }
.lv5-kpi { padding: 13px; border: 1px solid var(--lv5-line); border-radius: 13px; background: rgba(255,255,255,.02); }
.lv5-kpi-label {
  font-size: 9px; text-transform: uppercase; letter-spacing: .08em;
  color: var(--lv5-text-dim); margin-bottom: 8px; font-weight: 600;
}
.lv5-kpi-value { font-weight: 700; font-size: 22px; letter-spacing: -.02em; line-height: 1; }
.lv5-kpi-value.with-star { display: flex; align-items: baseline; gap: 4px; }
.lv5-star { color: #FFC93D; font-size: 14px; }
.lv5-kpi-trend { font-size: 10px; margin-top: 6px; color: #4ADE80; font-weight: 500; }
.lv5-kpi-trend.muted { color: var(--lv5-text-dim); }
.lv5-kpi.alert .lv5-kpi-value { color: #E8502A; }
.lv5-kpi-badge {
  background: #E8502A; color: white; font-size: 9px;
  padding: 2px 6px; border-radius: 6px; font-weight: 600; margin-left: 4px;
}
.lv5-dash-chart {
  padding: 18px; border: 1px solid var(--lv5-line); border-radius: 14px;
  background: rgba(255,255,255,.02); margin-bottom: 14px;
}
.lv5-dash-chart-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
.lv5-dash-chart-title { font-size: 12px; font-weight: 600; }
.lv5-dash-chart-sub { font-size: 10px; color: var(--lv5-text-dim); }
.lv5-chart-svg { width: 100%; height: 80px; }
.lv5-dash-insights {
  padding: 14px; border: 1px solid rgba(232,80,42,.25); border-radius: 14px;
  background: rgba(232,80,42,.05);
}
.lv5-dash-insights-head {
  display: flex; align-items: center; gap: 8px; margin-bottom: 10px;
  font-size: 11px; font-weight: 700; letter-spacing: .06em;
  text-transform: uppercase; color: #E8502A;
}
.lv5-dash-insights-head::before { content: "✦"; font-size: 12px; }
.lv5-dash-insight-item {
  display: flex; align-items: center; gap: 8px;
  padding: 6px 0; font-size: 12px;
  color: var(--lv5-text);
  border-bottom: 1px solid rgba(232,80,42,.1);
}
.lv5-dash-insight-item:last-child { border-bottom: none; }
.lv5-dash-insight-item::before { content: "●"; color: #E8502A; font-size: 8px; }
.lv5-dash-insight-item small { color: var(--lv5-text-dim); margin-left: auto; font-size: 10px; }

/* ═══ PRICING ═══ */
.lv5-pricing { padding: 120px 0 80px; position: relative; z-index: 5; }
.lv5-plans { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin-top: 60px; }
.lv5-plan {
  padding: 36px 30px; border: 1px solid var(--lv5-line); border-radius: 24px;
  background: rgba(255,255,255,.025);
  backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
  position: relative; display: flex; flex-direction: column;
  transition: transform .35s cubic-bezier(.2,.8,.2,1), border-color .3s;
  cursor: pointer;
}
.lv5-plan:hover { transform: translateY(-4px); }
.lv5-plan:not(.featured):hover { border-color: rgba(232,80,42,.4); }
.lv5-plan:focus-visible {
  outline: 2px solid rgba(232,80,42,.6);
  outline-offset: 4px;
}
.lv5-plan.featured {
  background: linear-gradient(160deg, rgba(232,80,42,.12) 0%, rgba(232,80,42,.04) 100%);
  border-color: rgba(232,80,42,.4);
  box-shadow: 0 30px 80px -20px rgba(232,80,42,.25);
}
.lv5-plan-eyebrow {
  font-size: 10px; text-transform: uppercase; letter-spacing: .14em;
  font-weight: 700; color: var(--lv5-text-dim); margin-bottom: 14px;
}
.lv5-plan.featured .lv5-plan-eyebrow { color: #E8502A; }
.lv5-plan-tag {
  position: absolute; top: -12px; left: 50%; transform: translateX(-50%);
  background: linear-gradient(135deg, #E8502A, #FF6B3D);
  color: white; padding: 6px 14px; border-radius: 100px;
  font-size: 11px; font-weight: 700; letter-spacing: .04em;
  text-transform: uppercase;
  box-shadow: 0 10px 24px -6px rgba(232,80,42,.6);
}
.lv5-plan-name { font-weight: 700; font-size: 28px; letter-spacing: -.025em; margin-bottom: 18px; }
.lv5-plan-price { display: flex; align-items: baseline; gap: 6px; margin-bottom: 4px; }
.lv5-amount { font-weight: 700; font-size: 54px; letter-spacing: -.04em; line-height: 1; }
.lv5-period { font-size: 14px; color: var(--lv5-text-dim); }
.lv5-plan-cta {
  display: block; text-align: center; width: 100%;
  padding: 13px 18px; border-radius: 12px;
  font-size: 14px; font-weight: 600;
  transition: all .2s; margin-bottom: 28px;
  font-family: inherit; cursor: pointer;
}
.lv5-plan-cta.ghost { background: rgba(255,255,255,.04); color: var(--lv5-text); border: 1px solid var(--lv5-line-2); }
.lv5-plan-cta.ghost:hover { background: rgba(255,255,255,.08); border-color: rgba(232,80,42,.4); }
.lv5-plan-cta.solid {
  background: linear-gradient(135deg, #E8502A, #FF6B3D);
  color: white; border: none;
  box-shadow: 0 12px 30px -8px rgba(232,80,42,.55), inset 0 1px 0 rgba(255,255,255,.2);
}
.lv5-plan-cta.solid:hover {
  transform: translateY(-1px);
  box-shadow: 0 16px 40px -8px rgba(232,80,42,.7);
}
.lv5-plan-features { display: flex; flex-direction: column; gap: 12px; flex: 1; }
.lv5-plan-features li {
  display: flex; align-items: flex-start; gap: 10px;
  font-size: 14px; line-height: 1.4; color: var(--lv5-text);
}
.lv5-plan-features li::before {
  content: ""; flex-shrink: 0;
  width: 18px; height: 18px; border-radius: 50%;
  background: rgba(232,80,42,.15);
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23E8502A' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'><polyline points='20 6 9 17 4 12'/></svg>");
  background-size: 11px; background-position: center; background-repeat: no-repeat;
  margin-top: 1px;
}
.lv5-pricing-bottom { display: flex; justify-content: center; gap: 32px; margin-top: 48px; flex-wrap: wrap; }
.lv5-pricing-bottom .lv5-trust-item { font-size: 14px; }

/* Group section */
.lv5-group {
  margin-top: 60px; padding: 48px 44px;
  border: 1px solid var(--lv5-line-2); border-radius: 28px;
  background: linear-gradient(140deg, rgba(255,255,255,.04) 0%, rgba(232,80,42,.06) 100%);
  backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
  display: grid; grid-template-columns: 1fr 1fr; gap: 48px; align-items: center;
  position: relative; overflow: hidden;
}
.lv5-group::before {
  content: ""; position: absolute;
  top: -100px; right: -100px; width: 400px; height: 400px;
  background: radial-gradient(circle, rgba(232,80,42,.2) 0%, transparent 60%);
  filter: blur(40px);
}
.lv5-group-content { position: relative; }
.lv5-group-eyebrow {
  font-size: 11px; text-transform: uppercase; letter-spacing: .14em;
  font-weight: 700; color: #E8502A; margin-bottom: 14px;
}
.lv5-group-content h3 {
  font-weight: 700; font-size: clamp(26px, 3vw, 36px); line-height: 1.1;
  letter-spacing: -.025em; margin-bottom: 14px;
}
.lv5-group-content p { font-size: 15px; line-height: 1.55; color: var(--lv5-text-dim); margin-bottom: 24px; }
.lv5-group-list { display: flex; flex-direction: column; gap: 12px; margin-bottom: 28px; }
.lv5-group-list li {
  display: flex; align-items: flex-start; gap: 10px;
  font-size: 14px; color: var(--lv5-text); line-height: 1.4;
}
.lv5-group-list li::before {
  content: ""; flex-shrink: 0;
  width: 18px; height: 18px; border-radius: 50%;
  background: rgba(232,80,42,.15);
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23E8502A' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'><polyline points='20 6 9 17 4 12'/></svg>");
  background-size: 11px; background-position: center; background-repeat: no-repeat;
  margin-top: 1px;
}
.lv5-group-cta {
  display: inline-flex; align-items: center; gap: 10px;
  color: #E8502A; font-weight: 600; font-size: 15px;
  padding: 12px 0; border-bottom: 1px solid #E8502A;
  transition: gap .2s;
}
.lv5-group-cta:hover { gap: 14px; }
.lv5-group-visual { position: relative; display: flex; justify-content: center; align-items: center; }
.lv5-group-tiles { display: grid; grid-template-columns: repeat(2, 1fr); gap: 14px; width: 100%; max-width: 340px; }
.lv5-gt {
  padding: 18px; border: 1px solid var(--lv5-line-2); border-radius: 16px;
  background: rgba(10,9,8,.4);
  backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px);
  text-align: center;
}
.lv5-gt-disc { font-weight: 700; font-size: 32px; letter-spacing: -.03em; color: #E8502A; line-height: 1; }
.lv5-gt-bonus { font-size: 18px; color: var(--lv5-text); }
.lv5-gt-range {
  font-size: 11px; color: var(--lv5-text-dim);
  margin-top: 6px; text-transform: uppercase; letter-spacing: .06em;
}

/* ═══ FAQ ═══ */
.lv5-faq { padding: 120px 0; position: relative; z-index: 5; }
.lv5-faq-list { max-width: 760px; margin: 0 auto; display: flex; flex-direction: column; gap: 12px; }
.lv5-faq-item {
  border: 1px solid var(--lv5-line); border-radius: 18px;
  background: rgba(255,255,255,.025);
  backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
  overflow: hidden; transition: border-color .25s;
}
.lv5-faq-item.open { border-color: rgba(232,80,42,.3); }
.lv5-faq-q {
  width: 100%; padding: 22px 26px;
  display: flex; justify-content: space-between; align-items: center; gap: 20px;
  font-weight: 700; font-size: 16px; letter-spacing: -.01em;
  line-height: 1.35; text-align: left; color: var(--lv5-text); background: transparent;
}
.lv5-faq-icon {
  flex-shrink: 0; width: 32px; height: 32px; border-radius: 50%;
  background: rgba(232,80,42,.12);
  display: flex; align-items: center; justify-content: center;
  color: #E8502A; font-size: 18px; font-weight: 400;
  transition: transform .3s cubic-bezier(.2,.8,.2,1), background .25s, color .25s;
}
.lv5-faq-item.open .lv5-faq-icon { transform: rotate(45deg); background: #E8502A; color: white; }
.lv5-faq-body { max-height: 0; overflow: hidden; transition: max-height .4s ease; }
.lv5-faq-item.open .lv5-faq-body { max-height: 1000px; }

/* FAQ sous veil orange (2026-07-05) — même traitement lisibilité que la
   section dashboard : textes en blanc + cards assombries en verre dépoli.
   Les transitions évitent le "saut" de couleur quand lv5-veil-high bascule. */
.lv5-faq .lv5-section-eyebrow,
.lv5-faq .lv5-section-head h2,
.lv5-faq .lv5-section-head h2 .lv5-dim,
.lv5-faq .lv5-section-head p,
.lv5-faq .lv5-faq-item,
.lv5-faq .lv5-faq-q,
.lv5-faq .lv5-faq-icon,
.lv5-faq .lv5-faq-body-inner {
  transition: color .3s ease, background .3s ease, border-color .3s ease;
}
body.lv5-veil-high .lv5-faq .lv5-section-eyebrow { color: #FFFFFF; text-shadow: 0 1px 8px rgba(0,0,0,.2); }
body.lv5-veil-high .lv5-faq .lv5-section-head h2 { color: #FFFFFF; text-shadow: 0 2px 12px rgba(0,0,0,.18); }
body.lv5-veil-high .lv5-faq .lv5-section-head h2 .lv5-dim { color: rgba(255,255,255,.78); }
body.lv5-veil-high .lv5-faq .lv5-section-head p { color: rgba(255,255,255,.92); }
body.lv5-veil-high .lv5-faq .lv5-faq-item {
  background: rgba(0,0,0,.18); border-color: rgba(255,255,255,.22);
  backdrop-filter: blur(20px);
}
body.lv5-veil-high .lv5-faq .lv5-faq-item.open { border-color: rgba(255,255,255,.4); }
body.lv5-veil-high .lv5-faq .lv5-faq-q { color: #FFFFFF; }
body.lv5-veil-high .lv5-faq .lv5-faq-icon {
  background: rgba(255,255,255,.22); color: #FFFFFF;
}
body.lv5-veil-high .lv5-faq .lv5-faq-item.open .lv5-faq-icon { background: rgba(255,255,255,.9); color: #C73E1D; }
body.lv5-veil-high .lv5-faq .lv5-faq-body-inner { color: rgba(255,255,255,.92); }
.lv5-faq-body-inner {
  padding: 0 26px 24px;
  font-size: 15px; line-height: 1.6; color: var(--lv5-text-dim);
}
.lv5-faq-body-inner strong { color: var(--lv5-text); }
.lv5-faq-body-inner em { color: var(--lv5-text); font-style: normal; font-weight: 500; }

.lv5-faq-contact {
  margin-top: 40px; text-align: center;
  padding: 32px; border: 1px solid var(--lv5-line); border-radius: 20px;
  background: rgba(255,255,255,.025);
  backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
  max-width: 560px; margin-left: auto; margin-right: auto;
}
.lv5-faq-contact h4 { font-weight: 700; font-size: 20px; letter-spacing: -.02em; margin-bottom: 8px; }
.lv5-faq-contact p { font-size: 14px; color: var(--lv5-text-dim); margin-bottom: 18px; }
.lv5-faq-contact a {
  color: #E8502A; font-weight: 600;
  display: inline-flex; align-items: center; gap: 8px;
  transition: gap .2s;
}
.lv5-faq-contact a:hover { gap: 12px; }
.lv5-faq-email { display: block; margin-top: 12px; font-size: 13px; color: var(--lv5-text-dim); }

/* Card contact FAQ sous veil orange (2026-07-05) — le lien orange devient
   illisible sur fond orange → blanc, et la card suit le verre assombri. */
.lv5-faq-contact, .lv5-faq-contact h4, .lv5-faq-contact p,
.lv5-faq-contact a, .lv5-faq-email {
  transition: color .3s ease, background .3s ease, border-color .3s ease;
}
body.lv5-veil-high .lv5-faq-contact {
  background: rgba(0,0,0,.18); border-color: rgba(255,255,255,.22);
}
body.lv5-veil-high .lv5-faq-contact h4 { color: #FFFFFF; }
body.lv5-veil-high .lv5-faq-contact p { color: rgba(255,255,255,.92); }
body.lv5-veil-high .lv5-faq-contact a { color: #FFFFFF; text-decoration: underline; text-underline-offset: 3px; }
body.lv5-veil-high .lv5-faq-email { color: rgba(255,255,255,.85); }

/* ═══ fadeUp + responsive ═══ */
@keyframes lv5-fadeUp {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

@media (max-width: 980px) {
  .lv5-hero {
    grid-template-columns: 1fr;
    gap: 48px;
    padding: 40px 0 70px;
  }
  .lv5-hero-text { text-align: center; }
  .lv5-hero-visual { min-height: auto; margin-top: 8px; }
  .lv5-sub { margin-left: auto; margin-right: auto; }
  .lv5-cta-row { justify-content: center; }
  .lv5-trust { justify-content: center; }
  .lv5-rps-card { margin-left: auto; margin-right: auto; }
  .lv5-hero-visual .lv5-phone { width: 280px; height: 570px; }
  .lv5-hero-visual .lv5-float-tr { top: 0; right: -10px; }
  .lv5-hero-visual .lv5-float-bl { bottom: 20px; left: -20px; }
}
@media (max-width: 960px) {
  .lv5-nav-pill { display: none; }
  .lv5-mobile-trigger { display: flex; }
  .lv5-nav-cta .lv5-btn-ghost { display: none; }
  .lv5-nav-cta .lv5-btn-rps { display: none; }
  .lv5-problem-intro { margin-bottom: 56px; }
  .lv5-problem-grid { grid-template-columns: 1fr; max-width: 480px; margin: 0 auto; }
  .lv5-steps { grid-template-columns: 1fr; max-width: 480px; margin: 0 auto; }
  .lv5-dash-grid { grid-template-columns: 1fr; gap: 50px; }
  .lv5-plans { grid-template-columns: 1fr; max-width: 440px; margin: 60px auto 0; }
  .lv5-group { grid-template-columns: 1fr; padding: 36px 28px; gap: 32px; }
}
@media (max-width: 520px) {
  .lv5-dash-kpis { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 480px) {
  .lv5-nav-cta .lv5-btn-primary { padding: 10px 16px; font-size: 13px; }
  /* Label "Revyo" : on garde le span visible sur mobile (Fix 1). */
  .lv5-logo span { font-size: 16px; }
}
@media (max-width: 380px) {
  .lv5-logo span { font-size: 14px; }
  .lv5-nav-cta .lv5-btn-primary { padding: 8px 14px; font-size: 12px; }
}

/* ═══════════════════════════════════════════════════════════════════════
   SCROLL-DRIVEN ANIMATIONS (PR feat/landing-scroll-anims-footer-lang)
   - A.1 Halo orange autour du téléphone (var --lv5-phone-halo)
   - A.2 Float-cards cascade (slide-in + fade au mount)
   - A.3 Parallax hero-visual (var --lv5-phone-parallax)
   - A.4 Reveal on scroll (.lv5-reveal → .is-visible)
   - A.5 Stagger enfants (.lv5-stagger-child cascade)
   - A.6 Counter-up KPIs (JS, mais styling stable ici)
   - A.7 Chart SVG trace (stroke-dashoffset animée via JS, styling baseline)

   Toutes les nouvelles animations RESPECTENT prefers-reduced-motion via la
   règle @media en fin de bloc (override final).
   ═══════════════════════════════════════════════════════════════════════ */

/* A.1 — Halo orange diffus derrière le téléphone */
:root { --lv5-phone-halo: 0; --lv5-phone-parallax: 0px; }
.lv5-phone-stage::before {
  content: "";
  position: absolute;
  inset: -20% -25%;
  background: radial-gradient(circle at 50% 55%, rgba(232,80,42,.18) 0%, rgba(232,80,42,.08) 35%, transparent 70%);
  filter: blur(40px);
  opacity: var(--lv5-phone-halo, 0);
  transition: opacity .25s linear;
  pointer-events: none;
  z-index: -1;
}
/* Mobile : halo plus intense (effet "macOS Vision" demandé) */
@media (max-width: 980px) {
  .lv5-phone-stage::before {
    inset: -25% -30%;
    background: radial-gradient(circle at 50% 50%, rgba(232,80,42,.32) 0%, rgba(232,80,42,.14) 40%, transparent 75%);
    filter: blur(50px);
  }
}

/* A.3 — Parallax appliqué au wrapper hero-visual.
   On utilise un wrapper interne implicite : on translate .lv5-phone-stage
   pour ne pas casser la coordonnée du halo (qui est inset:-20% du même élément). */
.lv5-hero-visual {
  transform: translateY(var(--lv5-phone-parallax, 0px));
  transition: transform .12s linear;
  will-change: transform;
}

/* A.2 — Float-cards apparition cascade. Décalées de 200ms entre elles.
   Les animations existantes lv5-floatA/B (oscillation continue) se chainent
   après via animation-delay (mais on garde leur boucle infinie). */
@keyframes lv5-float-cascade-tr {
  from { opacity: 0; transform: translate(20px, -10px); }
  to   { opacity: 1; transform: translate(0, 0); }
}
@keyframes lv5-float-cascade-bl {
  from { opacity: 0; transform: translate(-20px, 10px); }
  to   { opacity: 1; transform: translate(0, 0); }
}
.lv5-float-cascade { opacity: 0; }
.lv5-float-tr.lv5-float-cascade {
  /* d'abord cascade (0.7s), puis floatA en boucle après .8s de délai total */
  animation:
    lv5-float-cascade-tr .7s .6s cubic-bezier(.2,.8,.2,1) forwards,
    lv5-floatA 7s 1.3s ease-in-out infinite;
}
.lv5-float-bl.lv5-float-cascade {
  animation:
    lv5-float-cascade-bl .7s .8s cubic-bezier(.2,.8,.2,1) forwards,
    lv5-floatB 8s 1.5s ease-in-out infinite;
}

/* A.4 — Reveal on scroll */
.lv5-reveal {
  opacity: 0;
  transform: translateY(20px);
  transition: opacity .7s cubic-bezier(.2,.8,.2,1), transform .7s cubic-bezier(.2,.8,.2,1);
  will-change: opacity, transform;
}
.lv5-reveal.is-visible {
  opacity: 1;
  transform: translateY(0);
}

/* A.5 — Stagger enfants. Sélecteur ancêtre flexible pour fonctionner même
   quand le wrapper intermédiaire (.lv5-problem-grid, .lv5-steps, etc.) sépare
   parent .lv5-reveal et enfants .lv5-stagger-child. */
.lv5-reveal .lv5-stagger-child {
  opacity: 0;
  transform: translateY(16px);
  transition: opacity .55s cubic-bezier(.2,.8,.2,1), transform .55s cubic-bezier(.2,.8,.2,1);
}
.lv5-reveal.is-visible .lv5-stagger-child { opacity: 1; transform: translateY(0); }
/* Cascade 100ms entre enfants (jusqu'à 6 — couvre les 6 FAQ items max) */
.lv5-reveal.is-visible .lv5-stagger-child:nth-child(1) { transition-delay: .05s; }
.lv5-reveal.is-visible .lv5-stagger-child:nth-child(2) { transition-delay: .15s; }
.lv5-reveal.is-visible .lv5-stagger-child:nth-child(3) { transition-delay: .25s; }
.lv5-reveal.is-visible .lv5-stagger-child:nth-child(4) { transition-delay: .35s; }
.lv5-reveal.is-visible .lv5-stagger-child:nth-child(5) { transition-delay: .45s; }
.lv5-reveal.is-visible .lv5-stagger-child:nth-child(6) { transition-delay: .55s; }

/* Pricing (2026-07-05) — le head cascade d'abord (eyebrow .05s → titre .15s →
   sous-titre .25s via les délais génériques ci-dessus), les cards plans
   suivent après pour laisser le titre respirer. Spécificité supérieure au
   sélecteur générique → override propre des délais .05/.15/.25. */
.lv5-pricing.is-visible .lv5-plans .lv5-stagger-child:nth-child(1) { transition-delay: .3s; }
.lv5-pricing.is-visible .lv5-plans .lv5-stagger-child:nth-child(2) { transition-delay: .4s; }
.lv5-pricing.is-visible .lv5-plans .lv5-stagger-child:nth-child(3) { transition-delay: .5s; }

/* A.7 — Chart SVG trace : la longueur exacte est posée en JS via
   path.getTotalLength(). Ici on s'assure juste que la transition est propre.
   Pas de stroke-dasharray/offset CSS — c'est le JS qui pilote. */
.lv5-chart-line {
  /* will-change pour hint le compositeur GPU pendant l'anim */
  will-change: stroke-dashoffset;
}

/* ─── Reduced motion : on désactive TOUTES les nouvelles animations ─── */
@media (prefers-reduced-motion: reduce) {
  /* Fix 4 hero-glow : opacity figée à .35, pas d'animation au load */
  .lv5-hero::before {
    opacity: .35 !important;
    transition: none !important;
  }
  /* A.1 halo : on garde une opacity constante moyenne (effet figé doux) */
  .lv5-phone-stage::before {
    opacity: .35 !important;
    transition: none !important;
  }
  /* A.3 parallax désactivé */
  .lv5-hero-visual {
    transform: none !important;
    transition: none !important;
    will-change: auto;
  }
  /* A.2 float-cards : pas de cascade, visible direct, pas d'oscillation */
  .lv5-float-cascade {
    opacity: 1 !important;
    animation: none !important;
  }
  /* A.4 reveal : tout visible direct, pas de transition */
  .lv5-reveal {
    opacity: 1 !important;
    transform: none !important;
    transition: none !important;
    will-change: auto;
  }
  /* A.5 stagger : idem */
  .lv5-reveal .lv5-stagger-child {
    opacity: 1 !important;
    transform: none !important;
    transition: none !important;
  }
  /* A.7 chart : visible tout de suite */
  .lv5-chart-line {
    stroke-dasharray: none !important;
    stroke-dashoffset: 0 !important;
    transition: none !important;
    will-change: auto;
  }
}
`;
